// End-to-end encryption on this device (M28, docs/end-to-end-encryption.md):
// this install's key, the chat keys it can open, sealing outgoing messages
// and opening incoming ones. Everything that reaches the UI has already been
// opened here, so the rest of the app works with ordinary messages.
import { forgetRestKey } from './rest';
import { api, ApiError } from '../api';
import { t } from '../../i18n';
import { countClientFailure, reportError } from '../errors';
import { markUpdateRequired } from '../updateRequired';
import { rememberGatheringTitle } from '../gatheringTitles';
import {
  exportPublicKey,
  generateContentKey,
  generateDeviceKeyPair,
  messageAad,
  openEnvelope,
  sealEnvelope,
  securityCode,
  unwrapContentKey,
  wrapContentKey,
  type FileSecret,
} from './crypto';
import {
  appAadV2,
  groupAadV2,
  messageAadV2,
  revisionDecision,
  v1CeilingFrom,
  v1Decision,
  type GroupBinding,
} from './envelopeV2';
import { openText, sealText } from './rest';
import { gatheringContentSchema, pqGraceOverByDate } from '@twinden/shared';
import {
  acceptanceCeiling,
  pqAvailable,
  mlkemKeyPair,
  mlkemPublicKeyValid,
  pqSenderDecision,
  pqShouldRotate,
  securityCodeLine,
  unwrapContentKeyV2,
  v1CopyDecision,
  wrapContentKeyV2,
  wrappedKeyFormat,
  type MlkemKeyPair,
} from './pq';
import {
  deleteDeviceStore,
  deletePreviousDevice,
  deleteScopeInfo,
  loadDevice,
  loadPreviousDevices,
  loadScopeInfo,
  probeKeyStore,
  saveDevice,
  savePreviousDevice,
  loadTrustRecord,
  saveScopeInfo,
  updateTrustRecord,
  loadCeilingState,
  updateCeilingState,
  loadMlkem,
  saveMlkem,
  loadPqState,
  updatePqState,
  type KeptPq,
  type StoredDevice,
  type StoredMlkem,
} from './store';
import {
  activeDevices,
  afterLook,
  checkSendEpoch,
  checkWrapper,
  currentMembers,
  isRecorded,
  mergeDevices,
  newTrustRecord,
  epochTimesSane,
  normalizeTrustRecord,
  noteChatList as noteChatListFor,
  raiseWatermark,
  noteFamilyList as noteFamilyListFor,
  noteMembers,
  notePlainBefore,
  topEpoch,
  ownNotice,
  plainDecision,
  recordedMlkem,
  wrapperHasMlkem,
  type ListedDevice,
  type TrustRecord,
} from './trust';

/** What an envelope holds (D59). */
export interface Envelope {
  v: 1;
  text?: string;
  meta?: {
    fileName?: string;
    mimeType?: string;
    fileSize?: number;
    width?: number;
    height?: number;
    duration?: number;
    waveform?: number[];
    linkPreview?: unknown;
    /** The main file (files[0]) and its preview (files[1]). */
    file?: FileSecret;
    thumb?: FileSecret;
    /** A forwarded older attachment whose file was never encrypted. */
    plainFile?: boolean;
  };
  /** M60 (D219): an album's items, in order; `text` is its caption. */
  album?: AlbumItem[];
  /** M60 (D220): what a poll asks. Its votes are plain metadata. */
  poll?: { question: string; options: string[] };
  /** M71 (D282): a gathering's words. When and its replies are metadata. */
  gathering?: { title: string; place?: string; note?: string };
  /** M72 (D291): a group's picture, sealed like its description. */
  picture?: { mime: string; data: string };
}

/** One photo or video in an album (docs/chat-round-three.md §3). */
export interface AlbumItem {
  kind: 'image' | 'gif' | 'video';
  /** Index of its file in the message's `metadata.files`. */
  file: number;
  /** Index of its preview there, if it has one. */
  thumb?: number;
  fileKey: FileSecret;
  thumbKey?: FileSecret;
  mimeType: string;
  fileName: string;
  fileSize: number;
  width?: number;
  height?: number;
  duration?: number;
}

// Types whose content is their first file (M28). Anything else this app
// doesn't know shows as "update Twinden" rather than a file link (D222).
const FILE_TYPES = new Set(['image', 'gif', 'video', 'voice', 'file']);

/** Added to an opened message so the UI can show and re-seal it. */
export interface OpenedE2EE {
  envelope?: Envelope;
  /** No key for it on this device yet (D57), or `unverified`. */
  locked?: boolean;
  /**
   * Never shown, and never as from anyone; not waiting for a key either:
   * the key is here but it doesn't open as what it claims to be (M76,
   * D327), or plain text where only sealed content can be (D347).
   */
  unverified?: boolean;
  /**
   * Bound to its sender, type, epoch and revision (v2, D325); false for an
   * older v1 envelope the app opens but doesn't vouch for (D328).
   */
  bound?: boolean;
}

/** Whose key: a chat's (messages) or a family's (its apps, D65). */
export type ScopeKind = 'chat' | 'family';

let userId: string | null = null;
let device: StoredDevice | null = null;
let ready: Promise<void> | null = null;
const contentKeys = new Map<string, CryptoKey>();
const missingSince = new Map<string, number>();
const loading = new Map<string, Promise<ScopeKeys | null>>();
// What this device last learned about each chat's or family's keys. Sealing
// reuses it, so sending takes one round trip instead of fetching key details
// first (~270 ms each from India, 2026-10-22). The server refuses a stale
// epoch (STALE_KEY) or a needed rotation (ROTATION_REQUIRED), and the sender
// then forgets this (forgetScope) and seals again with fresh details.
const scopeInfo = new Map<string, ScopeKeys>();

type ScopeKeys = Awaited<ReturnType<typeof api.getScopeKeys>>['data'];

// ---- Post-quantum key wrapping (M78, docs/post-quantum.md) -----------------

// This device's ML-KEM-768 key (D336), opened into memory for the page's
// life (§4.4), and whether the server has accepted it.
let mlkem: MlkemKeyPair | null = null;
// The server accepted it (registration, a PUT, or its own list). Sticky
// (review H1): once accepted, a server listing it no longer can't switch
// this device's downgrade protection off; it is repaired (`PUT`) instead.
let mlkemPublished = false;
// Answers' ML-KEM keys that failed FIPS 203's check, by the device's P-256
// key: such a device gets no copy at all, not v1 (review M1).
const pqInvalid = new Set<string>();
// A keys answer said the grace is over (§6.5); so does the app's own date.
let graceOverSeen = false;
// Per scope: the PQ ceiling and the once-only rotation, as kept (D339).
const pqStates = new Map<string, KeptPq>();
// Per scope: the format of this device's copy of the current epoch.
const pqCurrentCopy = new Map<string, 'v1' | 'v2'>();
// Scopes where a v2 copy for this device didn't open: rotate (§4.3).
const pqFailed = new Set<string>();
// `<counter>|<what>`: each counted once per launch (D338).
const pqCounted = new Set<string>();
// ML-KEM keys from answers already checked (FIPS 203 §7.2): valid or not.
const pqChecked = new Map<string, boolean>();

const MLKEM_PENDING = 'pending';

function countPqOnce(
  name:
    | 'e2ee_pq_downgrade'
    | 'e2ee_pq_unwrap_failed'
    | 'e2ee_pq_key_missing'
    | 'e2ee_pq_key_changed'
    | 'e2ee_pq_own_key_mismatch'
    | 'e2ee_pq_key_unavailable',
  what: string
) {
  const id = `${name}|${what}`;
  if (pqCounted.has(id)) return;
  pqCounted.add(id);
  countClientFailure(name);
}

/** This device has an ML-KEM key the server accepted (D339's "I have one"). */
function iHaveMlkem(): boolean {
  return mlkem !== null && mlkemPublished;
}

/** The grace is over by this app's date or a keys answer (§6.5). */
export function pqGraceIsOver(): boolean {
  return graceOverSeen || pqGraceOverByDate();
}

function noteGraceAnswer(over: boolean | undefined) {
  if (over !== true) return;
  graceOverSeen = true;
  // Only a device whose own key couldn't be made or published is left out:
  // it can read, not write (§6.5 "Apps" 2).
  if (!iHaveMlkem()) markUpdateRequired();
}

/**
 * An answer's ML-KEM key, checked (FIPS 203's encapsulation-key check) and
 * treated as missing, counted, when it fails (§4.2). undefined stays
 * undefined: the answer said nothing.
 */
function checkedMlkem(
  ek: string | null | undefined,
  devicePublicKey: string
): string | null | undefined {
  if (typeof ek !== 'string') return ek;
  let valid = pqChecked.get(ek);
  if (valid === undefined) {
    valid = mlkemPublicKeyValid(ek);
    if (pqChecked.size > 2000) pqChecked.clear();
    pqChecked.set(ek, valid);
  }
  if (valid) return ek;
  countPqOnce('e2ee_pq_key_missing', devicePublicKey);
  pqInvalid.add(devicePublicKey);
  return null;
}

/**
 * This device's own ML-KEM key as an answer should list it (review M2):
 * always the key it holds once accepted, never what the server says;
 * left out (nothing learned) before.
 */
function ownMlkem(): string | undefined {
  return mlkem && mlkemPublished ? mlkem.publicKey : undefined;
}

/** This device's post-quantum status, for the codes sheet (review M5). */
export function thisDeviceQuantumSafe(): boolean {
  return mlkem !== null && mlkemPublished;
}

const SEED_AAD = (forUser: string) =>
  new TextEncoder().encode(`twinden mlkem seed v1|${forUser}`);

/** Seals the seed under a fresh non-extractable key and keeps it (§4.1). */
async function keepMlkem(
  forUser: string,
  deviceId: string,
  pair: MlkemKeyPair,
  published: boolean,
  fromRegistration: boolean
): Promise<void> {
  await saveMlkem(
    forUser,
    await sealMlkem(forUser, deviceId, pair, published, fromRegistration)
  );
}

async function sealMlkem(
  forUser: string,
  deviceId: string,
  pair: MlkemKeyPair,
  published: boolean,
  fromRegistration: boolean
): Promise<StoredMlkem> {
  const subtle = globalThis.crypto.subtle;
  const sealKey = await subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
  const iv = globalThis.crypto.getRandomValues(
    new Uint8Array(new ArrayBuffer(12))
  );
  const sealed = await subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: SEED_AAD(forUser) },
    sealKey,
    new Uint8Array(pair.seed)
  );
  return {
    deviceId,
    publicKey: pair.publicKey,
    sealKey,
    iv,
    sealed,
    published,
    fromRegistration,
  };
}

async function openMlkem(
  forUser: string,
  stored: StoredMlkem
): Promise<MlkemKeyPair> {
  const seed = new Uint8Array(
    await globalThis.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: stored.iv, additionalData: SEED_AAD(forUser) },
      stored.sealKey,
      stored.sealed
    )
  );
  const pair = mlkemKeyPair(seed);
  if (pair.publicKey !== stored.publicKey) throw new Error('E2EE_MLKEM_SEED');
  return pair;
}

/** The highest epoch held per scope, from the server (review M4). */
type Held = Array<{ scope: 'chat' | 'family'; scopeId: string; epoch: number }>;

/**
 * This device's ML-KEM key at start (D336, §11 item 1): opened from the
 * store, or made now and published once (`PUT`, an older device), tried
 * again at every start until the server accepts it. `serverRow` is this
 * device's own row as the server lists it (undefined offline), the
 * self-check (§4.3).
 *
 * - Acceptance is sticky (review H1): a server that later lists the row
 *   without the key (or with another) gets it again by `PUT`, counted as a
 *   mismatch; this device keeps refusing downgrades.
 * - At acceptance the PQ ceilings of every scope are written from one
 *   listing of the copies this device holds (review M4); a device that
 *   registered with its key needs none (no ceiling is 0).
 * - `'replace'`: the server lists an ML-KEM key for this device that it
 *   doesn't hold or can't open (a lost store, a reinstall), or the server
 *   has another key for it. A new key is never made for the old row: the
 *   caller registers a new device, D262's path (reviews H2, Low 6).
 */
async function setUpMlkem(
  forUser: string,
  me: StoredDevice,
  serverRow: { mlkemPublicKey?: string | null } | undefined
): Promise<'ok' | 'replace'> {
  if (!pqAvailable()) return 'ok';
  const known = serverRow !== undefined;
  const serverKey = serverRow?.mlkemPublicKey ?? null;
  let stored: StoredMlkem | null = null;
  try {
    stored = await loadMlkem(forUser);
  } catch {
    // Not readable now: nothing is made in its place. Next start.
    countPqOnce('e2ee_pq_key_unavailable', 'load');
    return 'ok';
  }
  let pair: MlkemKeyPair | null = null;
  let accepted = false;
  let registeredWith = false;
  if (
    stored &&
    (stored.deviceId === me.id || stored.deviceId === MLKEM_PENDING)
  ) {
    try {
      pair = await openMlkem(forUser, stored);
      accepted = stored.published;
      registeredWith = stored.fromRegistration;
    } catch {
      pair = null;
    }
  }
  if (!pair) {
    if (known && serverKey) {
      countPqOnce('e2ee_pq_key_unavailable', 'lost');
      return 'replace';
    }
    // A row that ever had a key accepted (kept as published, or in my own
    // record) never gets a new one: a new device instead (final review,
    // point 6).
    const ownRecorded =
      trust && recordedMlkem(trust, forUser, me.publicKey) !== null;
    if ((stored?.deviceId === me.id && stored.published) || ownRecorded) {
      countPqOnce('e2ee_pq_key_unavailable', 'lost');
      return 'replace';
    }
    try {
      pair = mlkemKeyPair();
      await keepMlkem(forUser, me.id, pair, false, false);
    } catch {
      countPqOnce('e2ee_pq_key_unavailable', 'make');
      return 'ok';
    }
  }
  let held: Held | null = null;
  const wasAccepted = accepted;
  if (known && serverKey !== pair.publicKey) {
    if (accepted) countPqOnce('e2ee_pq_own_key_mismatch', me.id);
    if (serverKey && !accepted) {
      // Another key is set for this device: this one can never be.
      countPqOnce('e2ee_pq_key_unavailable', 'set');
      return 'replace';
    }
    try {
      const put = await api.setMlkemKey(me.id, pair.publicKey);
      if (!accepted) {
        held = put.data.held ?? [];
        accepted = true;
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MLKEM_KEY_SET') {
        countPqOnce('e2ee_pq_key_unavailable', 'set');
        if (!accepted) return 'replace';
      }
      // Offline or an older server: tried again at the next start. Until
      // accepted, the key isn't used.
    }
  } else if (known && !accepted) {
    // Listed with my key: registration took it, or a PUT whose answer was
    // lost did. Accepted only once the ceilings are written (point 5): a
    // listing that can't be had now is tried again at the next start.
    if (registeredWith) {
      accepted = true;
      held = [];
    } else {
      held = await api
        .getHeldKeys(me.id)
        .then((r) => r.data.held)
        .catch(() => null);
      accepted = held !== null;
    }
  }
  if (userId !== forUser || device?.id !== me.id) return 'ok';
  // In memory before the ceilings: their check opens this device's copies
  // (a v2 one needs the key); the PQ rules don't apply until accepted.
  mlkem = pair;
  if (accepted && !wasAccepted && held) {
    await writeAcceptanceCeilings(held);
  }
  if (accepted && (!wasAccepted || stored?.deviceId !== me.id)) {
    await keepMlkem(forUser, me.id, pair, true, registeredWith).catch(
      () => undefined
    );
  }
  if (userId !== forUser || device?.id !== me.id) return 'ok';
  mlkemPublished = accepted;
  return 'ok';
}

/**
 * Every scope's PQ ceiling at acceptance (D339, review M4, final review
 * point 4): the server's `held`, capped at what this device had already
 * opened there or opens now from the scope's own keys answer. A server
 * inflating `held` gains nothing.
 */
async function writeAcceptanceCeilings(held: Held) {
  for (const row of held) {
    const scope = `${row.scope}:${row.scopeId}`;
    const openedLocally = trust ? topEpoch(trust, scope) : 0;
    let opensNow: number | null = null;
    const me = device;
    if (row.epoch > openedLocally && me) {
      // The copies this device opens now: the server can list copies but
      // can't make one that opens, so only these count. Opened here only
      // as evidence (setup is still running, so not through fetchScope).
      const info = await api
        .getScopeKeys(row.scope, row.scopeId, me.id)
        .then((r) => r.data)
        .catch(() => null);
      for (const entry of info?.keys ?? []) {
        if (entry.epoch > row.epoch || entry.epoch <= (opensNow ?? 0)) continue;
        const scopeOf = {
          kind: row.scope,
          scopeId: row.scopeId,
          epoch: entry.epoch,
        };
        const format = wrappedKeyFormat(entry.wrappedKey);
        const opened = await (
          format === 'v2' && mlkem
            ? unwrapContentKeyV2({
                wrapped: entry.wrappedKey,
                myPrivate: me.privateKey,
                mySpki: me.publicKey,
                myMlkem: mlkem,
                wrapperSpki: entry.wrappedByPublicKey,
                scope: scopeOf,
              })
            : format === 'v1'
              ? unwrapContentKey(
                  entry.wrappedKey,
                  me.privateKey,
                  entry.wrappedByPublicKey,
                  wrapScope(row.scope, row.scopeId, entry.epoch)
                )
              : Promise.reject(new Error('E2EE_WRAP_FORMAT'))
        )
          .then(() => true)
          .catch(() => false);
        if (opened) opensNow = entry.epoch;
      }
    }
    await notePqCeiling(
      scope,
      acceptanceCeiling({ held: row.epoch, openedLocally, opensNow })
    );
  }
}

/** Where a scope's PQ state is kept: per device key (a new key, a fresh start). */
const pqStateKey = (scope: string) => `${device?.id ?? ''}:${scope}`;

/** A scope's kept PQ state, loaded once a visit. */
async function pqStateFor(scope: string): Promise<KeptPq> {
  const at = pqStateKey(scope);
  const known = pqStates.get(at);
  if (known) return known;
  const owner = userId;
  const kept = owner
    ? ((await loadPqState(owner, at).catch(() => null)) ?? {})
    : {};
  pqStates.set(at, kept);
  return kept;
}

async function changePqState(scope: string, change: (s: KeptPq) => KeptPq) {
  const owner = userId;
  const at = pqStateKey(scope);
  const next = change(pqStates.get(at) ?? {});
  pqStates.set(at, next);
  if (!owner) return;
  try {
    const stored = await updatePqState(owner, at, (s) => change(s ?? {}));
    if (userId === owner) pqStates.set(at, stored);
  } catch {
    // Kept in memory for this visit.
  }
}

/**
 * A scope's PQ ceiling (D339, §6.2), written once, at acceptance, from the
 * listing of the copies this device then held (review M4). A scope without
 * one (joined later, or a device that registered with its key) is 0.
 */
async function notePqCeiling(scope: string, ceiling: number) {
  await pqStateFor(scope);
  await changePqState(scope, (s) =>
    s.ceiling !== undefined ? s : { ...s, ceiling }
  );
}

/** Does this scope need the backstop rotation before the next send (§6.3)? */
function pqRotationDue(scope: string): boolean {
  if (!iHaveMlkem()) return false;
  const kept = pqStates.get(pqStateKey(scope)) ?? {};
  if (pqFailed.has(scope) && !kept.rotated) return true;
  return pqShouldRotate({
    iHaveMlkem: true,
    myCurrentCopy: pqCurrentCopy.get(scope) ?? null,
    alreadyRotated: kept.rotated === true,
  });
}

/**
 * Wraps a content key for one device as the record says (D339, vector
 * senderCases): v2 to the recorded ML-KEM key, v1 to a device without one
 * during the grace, nothing after it.
 */
async function wrapFor(
  key: CryptoKey,
  target: {
    userId?: string;
    publicKey: string;
    mlkemPublicKey?: string | null;
  },
  kind: ScopeKind,
  id: string,
  epoch: number
): Promise<string | null> {
  const me = device;
  if (!me) throw new Error('End-to-end encryption isn’t ready on this device');
  // This device wraps to itself with its own key, whatever is listed or
  // recorded (review M2).
  if (target.publicKey === me.publicKey) {
    const own = ownMlkem();
    if (own) {
      return wrapContentKeyV2(
        key,
        me.privateKey,
        me.publicKey,
        me.publicKey,
        own,
        { kind, scopeId: id, epoch }
      );
    }
    if (pqGraceIsOver()) return null;
    return wrapContentKey(
      key,
      me.privateKey,
      me.publicKey,
      wrapScope(kind, id, epoch)
    );
  }
  const recorded =
    trust && target.userId
      ? recordedMlkem(trust, target.userId, target.publicKey)
      : null;
  const listed = checkedMlkem(target.mlkemPublicKey, target.publicKey);
  // A key failing FIPS 203's check, with none recorded: no copy, counted,
  // rather than v1 the server would refuse with the whole epoch (M1).
  if (recorded === null && pqInvalid.has(target.publicKey)) {
    countPqOnce('e2ee_pq_key_missing', target.publicKey);
    return null;
  }
  const decision = pqSenderDecision({
    recorded,
    // An answer that says nothing about it (an older server, an offline
    // copy) isn't a server leaving it out.
    answer: listed === undefined ? recorded : listed,
    graceOver: pqGraceIsOver(),
  });
  if (decision.count) countPqOnce(decision.count, target.publicKey);
  if (decision.format === 'skip') return null;
  if (decision.format === 'v2') {
    const ek = decision.key === 'recorded' ? recorded : listed;
    if (!ek) return null;
    return wrapContentKeyV2(
      key,
      me.privateKey,
      me.publicKey,
      target.publicKey,
      ek,
      { kind, scopeId: id, epoch }
    );
  }
  return wrapContentKey(
    key,
    me.privateKey,
    target.publicKey,
    wrapScope(kind, id, epoch)
  );
}

/** Lets the page breathe between ML-KEM encapsulations (§9). */
const breathe = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

const keyId = (kind: ScopeKind, id: string, epoch: number) =>
  `${kind}:${id}:${epoch}`;
const wrapScope = (kind: ScopeKind, id: string, epoch: number) => ({
  scopeId: id,
  epoch,
  kind,
});

function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  return 'Browser';
}

// ---- Can this browser keep a key? (D262) -----------------------------------

/**
 * Why messages may stay locked on this device, for the banner (D262):
 * `blocked` — a browser setting keeps Twinden from the site data the key
 * lives in, so no key was registered; `broken` — the browser's storage for
 * this site doesn't work (a damaged backing store; only clearing the site's
 * data mends it), so no key was registered; `slow` — the storage didn't
 * open in time, retried once, then "reload to try again"; `tooMany` — the
 * server refused yet another device key for this account today;
 * `ephemeral` — the key is forgotten when the site is closed; `replaced` —
 * the stored key couldn't be used and a fresh one was registered, so the
 * keys have to come over again; `unsupported` — WebCrypto itself fails here
 * (even a fresh key pair can't do a round trip), so the stored key is kept
 * and another browser is the way out; `unusable` — the stored key works on
 * its own but isn't the one the server lists for it, an ambiguous case in
 * which it is kept and signing out and in again mends it.
 */
export type KeyStorageState =
  | 'ok'
  | 'blocked'
  | 'broken'
  | 'slow'
  | 'tooMany'
  | 'ephemeral'
  | 'replaced'
  | 'unsupported'
  | 'unusable';

let storageState: KeyStorageState = 'ok';
const storageListeners = new Set<() => void>();

function setStorageState(next: KeyStorageState) {
  if (storageState === next) return;
  storageState = next;
  storageListeners.forEach((listener) => listener());
}

export function keyStorageState(): KeyStorageState {
  return storageState;
}

/** Runs `listener` whenever `keyStorageState()` changes; returns unsubscribe. */
export function onKeyStorageState(listener: () => void): () => void {
  storageListeners.add(listener);
  return () => {
    storageListeners.delete(listener);
  };
}

// sessionStorage: this tab already registered a key for this user. A store
// that forgets keys between visits gets one key per visit at most, and one
// that loses them mid-visit gets no second one (D262).
const registeredInTab = (forUser: string) =>
  `twinden:e2ee:registered:${forUser}`;
// localStorage: when an unusable key was last replaced. A browser that
// mangles every key it stores would otherwise register one per load.
const replacedAt = (forUser: string) => `twinden:e2ee:replaced:${forUser}`;
const REPLACE_AGAIN_AFTER_MS = 24 * 60 * 60 * 1000;

function readFlag(storage: 'session' | 'local', key: string): string | null {
  try {
    return (storage === 'session' ? sessionStorage : localStorage).getItem(key);
  } catch {
    return null;
  }
}

function writeFlag(storage: 'session' | 'local', key: string, value: string) {
  try {
    (storage === 'session' ? sessionStorage : localStorage).setItem(key, value);
  } catch {
    // Without web storage the guards just don't apply.
  }
}

/**
 * One ECDH round trip (D262): a content key wrapped by a throwaway pair to
 * `publicKey` must unwrap with `privateKey`. A private key that can't open
 * what is wrapped to the public key the server hands out would leave every
 * key another device wraps for this one unopenable.
 */
async function roundTrip(
  privateKey: CryptoKey,
  publicKey: string
): Promise<boolean> {
  try {
    const probe = await generateDeviceKeyPair();
    const probePublic = await exportPublicKey(probe.publicKey);
    const scope = { scopeId: 'self-test', epoch: 1, kind: 'chat' as const };
    const wrapped = await wrapContentKey(
      await generateContentKey(),
      probe.privateKey,
      publicKey,
      scope
    );
    await unwrapContentKey(wrapped, privateKey, probePublic, scope);
    return true;
  } catch {
    return false;
  }
}

/** Does WebCrypto work here at all? A round trip with a fresh pair. */
async function cryptoWorks(): Promise<boolean> {
  try {
    const pair = await generateDeviceKeyPair();
    return await roundTrip(
      pair.privateKey,
      await exportPublicKey(pair.publicKey)
    );
  } catch {
    return false;
  }
}

const isCryptoKey = (value: unknown): value is CryptoKey =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as CryptoKey).type === 'string';

const malformed = (stored: StoredDevice) =>
  typeof stored.id !== 'string' ||
  typeof stored.publicKey !== 'string' ||
  typeof stored.privateKey !== 'object' ||
  stored.privateKey === null;

// A failed test runs again after this long: a WebCrypto hiccup or a tab
// torn down mid-test must never cost a good key.
export const SELF_TEST_AGAIN_MS = 500;
const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));
const twice = async (test: () => Promise<boolean>) =>
  (await test()) || (await wait(SELF_TEST_AGAIN_MS).then(test));

/**
 * What to do with the stored key (D262):
 * - `keep` — it works.
 * - `repair` — its private key opens what is wrapped to the server's public
 *   key for its id; only the stored public-key string was wrong.
 * - `replace` — WebCrypto works here (a fresh pair passes) but the stored
 *   key fails its round trip **twice**, or the record is malformed.
 *   `verified`: it also failed against the server's public key, so it can't
 *   have opened any copy, and its row may go as soon as the fresh key opens
 *   a share; otherwise the row is kept a week.
 * - `unsupported` — even a fresh pair fails, twice: the browser is the
 *   problem; the key is kept.
 * - `ambiguous` — the stored pair works but the server lists another public
 *   key, which the private key can't open: kept, and the user is told to
 *   sign out and in again.
 */
type Verdict =
  | { action: 'keep' }
  | { action: 'repair'; publicKey: string }
  | { action: 'replace'; reason: string; verified: boolean }
  | { action: 'unsupported' }
  | { action: 'ambiguous' };

async function judgeKey(
  stored: StoredDevice,
  serverPublicKey: string | undefined
): Promise<Verdict> {
  if (!(await twice(cryptoWorks))) {
    reportError({
      message: 'e2ee: WebCrypto fails with a fresh key pair; device key kept',
    });
    return { action: 'unsupported' };
  }
  if (malformed(stored)) {
    // A private key may survive a damaged record: tried against the
    // server's key before it is called useless.
    const hasPrivate = isCryptoKey(stored.privateKey);
    if (hasPrivate && serverPublicKey !== undefined) {
      if (await twice(() => roundTrip(stored.privateKey, serverPublicKey))) {
        return { action: 'repair', publicKey: serverPublicKey };
      }
      return { action: 'replace', reason: 'malformed record', verified: true };
    }
    return {
      action: 'replace',
      reason: 'malformed record',
      verified: !hasPrivate,
    };
  }
  const differs =
    serverPublicKey !== undefined && serverPublicKey !== stored.publicKey;
  if (differs) {
    if (await twice(() => roundTrip(stored.privateKey, serverPublicKey))) {
      return { action: 'repair', publicKey: serverPublicKey };
    }
    if (await twice(() => roundTrip(stored.privateKey, stored.publicKey))) {
      reportError({
        message:
          'e2ee: server lists another public key for this device id; key kept (its own pair works)',
      });
      return { action: 'ambiguous' };
    }
    return {
      action: 'replace',
      reason: 'self-test failed twice, also against the server’s key',
      verified: true,
    };
  }
  if (await twice(() => roundTrip(stored.privateKey, stored.publicKey))) {
    return { action: 'keep' };
  }
  return {
    action: 'replace',
    reason: `self-test failed twice (server key ${
      serverPublicKey === undefined ? 'unknown' : 'matches'
    })`,
    verified: serverPublicKey !== undefined,
  };
}

/**
 * The stored public-key string was wrong but the private key is the
 * server's pair (D262): fix the string. `self` is set aside first, as
 * everywhere it is overwritten, and the aside dropped once the fix is in.
 */
async function repairPublicKey(
  forUser: string,
  stored: StoredDevice,
  publicKey: string
): Promise<StoredDevice> {
  const repaired = { ...stored, publicKey };
  try {
    await savePreviousDevice(forUser, { ...stored, replacedAt: Date.now() });
    await saveDevice(forUser, repaired);
    await deletePreviousDevice(forUser, stored.id).catch(() => undefined);
    reportError({
      message: 'e2ee: stored public key repaired from the server’s',
    });
  } catch {
    // The private key is the same either way; the next load tries again.
  }
  return repaired;
}

// One tab at a time decides about this user's key: two tabs starting with
// the same unusable key would otherwise both replace it (and one fresh key
// would be an orphan). Web Locks where the browser has them.
const KEY_LOCK = 'twinden-e2ee-key';
async function withKeyLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return run();
  let ran = false;
  try {
    return (await locks.request(KEY_LOCK, async () => {
      ran = true;
      return run();
    })) as T;
  } catch (err) {
    // A sandboxed or opaque origin can refuse the lock itself: go without,
    // as a browser with no Web Locks does. `run`'s own failure is its own.
    if (ran) throw err;
    return run();
  }
}

/**
 * Registers a fresh key in place of one this device can't use (D262), at
 * most once a day. Nothing is destroyed: the old record is kept beside the
 * new one (`previous`), and its server row stays until the fresh key has
 * received its first share, or a week has passed (`retirePreviousKey`), so
 * a wrong call can still be undone. Returns null when the guard refuses,
 * and then says the browser is the problem.
 */
async function replaceUnusableKey(
  forUser: string,
  old: StoredDevice,
  reason: string,
  verified: boolean,
  /** Its ML-KEM key was lost (M78): no banner state from the daily guard. */
  pqLost = false
): Promise<StoredDevice | null> {
  const last = Number(readFlag('local', replacedAt(forUser)));
  if (last > 0 && Date.now() - last < REPLACE_AGAIN_AFTER_MS) {
    reportError({
      message: `e2ee: device key unusable again (${reason}); not replacing twice in a day`,
    });
    // The browser's storage isn't the problem when the ML-KEM key was
    // lost (final review, point 8): no "blocked" banner.
    if (!pqLost) setStorageState('blocked');
    return null;
  }
  reportError({
    message: `e2ee: device key unusable (${reason}); registering a new one`,
  });
  try {
    // The old record is set aside first: if it can't be kept, nothing is
    // replaced (a key this device can't put back would be gone for good).
    await savePreviousDevice(forUser, {
      ...old,
      replacedAt: Date.now(),
      verified,
      ...(pqLost ? { pqLost: true } : {}),
    });
  } catch (err) {
    reportError({
      message: `e2ee: replaced key could not be set aside (${(err as Error)?.name ?? 'Error'}); key kept`,
    });
    // A store that only answered late is slow, not broken: the
    // clear-site-data advice would delete the key still in `self`.
    if (isTimeout(err)) goSlow(forUser, 'set aside');
    else setStorageState('broken');
    return null;
  }
  let fresh: StoredDevice;
  try {
    fresh = await register(forUser);
  } catch (err) {
    // Nothing was replaced: `self` is still the old record.
    await deletePreviousDevice(forUser, old.id).catch(() => undefined);
    throw err;
  }
  // The day's budget is spent only by a replacement that happened: a
  // registration that failed (offline, 429) must not block tomorrow's.
  writeFlag('local', replacedAt(forUser), String(Date.now()));
  previousPending = true;
  setStorageState('replaced');
  return fresh;
}

// How long a replaced key's server row is kept at most (D262).
export const PREVIOUS_KEEP_MS = 7 * 24 * 60 * 60 * 1000;

// A replaced key (D262) is still set aside, waiting for its successor's
// first share before its server row may go.
let previousPending = false;

/**
 * Deletes a replaced key's server row once the fresh key has opened its
 * first share (`now`: a copy wrapped for it unwrapped in `fetchScope`) —
 * only for a key `verified` unable to open anything — or after a week
 * regardless; until then the swap can be undone. The row's wrapped copies
 * go with it (cascade), so this is the only place the self-heal deletes
 * anything. A record set aside because the server had lost its row
 * (`lost`) is never deleted there; it is dropped here after a week, and
 * only while the server still doesn't list it.
 */
async function retirePreviousKey(forUser: string, now = false) {
  try {
    // Compared with what `self` holds, not the in-memory key: `setUp` may
    // have stopped early with no `device` (slow, the daily guard).
    const selfId = (await loadDevice(forUser))?.id;
    let left = 0;
    let listed: Map<string, string> | null = null;
    const serverKeys = async () =>
      (listed ??= new Map(
        (await api.getUserDeviceKeys(forUser)).data.devices.map((d) => [
          d.id,
          d.publicKey,
        ])
      ));
    for (const old of await loadPreviousDevices(forUser)) {
      // Kept for good: its private key opens the server's key for its id.
      if (old.keep && !old.pqLost) continue;
      if (typeof old.id === 'string' && old.id === selfId) {
        // Put back by hand: it's the key in use again, not a previous one.
        await deletePreviousDevice(forUser, old.id);
        continue;
      }
      const weekOld = Date.now() - old.replacedAt >= PREVIOUS_KEEP_MS;
      if (old.lost) {
        if (!weekOld) {
          left += 1;
          continue;
        }
        if ((await serverKeys()).has(old.id)) {
          // The server knows it after all: keep it, never delete.
          left += 1;
          continue;
        }
        await deletePreviousDevice(forUser, old.id);
        continue;
      }
      if (!weekOld && !(now && old.verified) && !old.pqLost) {
        left += 1;
        continue;
      }
      if (
        !old.pqLost &&
        !old.verified &&
        typeof old.id === 'string' &&
        isCryptoKey(old.privateKey)
      ) {
        // Judged while the server's key was unknown: before its copies are
        // deleted, make sure this private key can't open them after all.
        const serverPublic = (await serverKeys()).get(old.id);
        if (
          serverPublic !== undefined &&
          (await roundTrip(old.privateKey, serverPublic))
        ) {
          reportError({
            message:
              'e2ee: a replaced device key opens the server’s key for its id; kept, not deleted',
          });
          await savePreviousDevice(forUser, { ...old, keep: true });
          continue;
        }
      }
      // A malformed record without a string id has no row to delete.
      if (typeof old.id === 'string') {
        try {
          await api.deleteDeviceKey(old.id);
        } catch (err) {
          // Already gone is done; anything else (offline) is tried next time.
          if (!(err instanceof ApiError && err.status === 404)) {
            left += 1;
            continue;
          }
        }
      }
      await deletePreviousDevice(forUser, old.id);
    }
    if (userId === forUser) previousPending = left > 0;
  } catch {
    // Next load.
  }
}

/**
 * Sets aside whatever `self` holds before something overwrites it (D262):
 * no path may overwrite `self` without first durably keeping it under
 * `previous:<id>`. Throws when that can't be done.
 */
async function setAsideSelf(forUser: string, lost: boolean) {
  const current = await loadDevice(forUser);
  if (!current) return;
  await savePreviousDevice(forUser, {
    ...current,
    replacedAt: Date.now(),
    lost,
  });
}

// A slow store (D262) is probed once more after this long; a cold start or
// an old phone may just have needed longer than the bound.
export const SLOW_RETRY_MS = 30_000;
let slowRetried = false;
let slowTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The store didn't answer in time (D262): not broken, just late. Try once
 * more in a while, and never advise clearing site data (that would delete
 * a good key). Views locked meanwhile reopen when the retry has a key.
 */
function goSlow(forUser: string, where: string) {
  setStorageState('slow');
  if (!slowRetried) {
    slowRetried = true;
    slowTimer = setTimeout(() => {
      slowTimer = null;
      if (userId !== forUser) return;
      ready = setUp(forUser)
        .then(() => {
          if (userId === forUser && device) {
            arrivedListeners.forEach((listener) => listener());
          }
        })
        .catch((err) => {
          console.warn('[e2ee] device key setup failed', err);
        });
    }, SLOW_RETRY_MS);
  } else {
    reportError({ message: `e2ee: key store slow twice (${where})` });
  }
}

const isTimeout = (err: unknown) =>
  (err as { name?: unknown } | null)?.name === 'TimeoutError';

async function setUp(forUser: string) {
  // No key is registered until the store that would hold it works (D262):
  // a browser blocking site data would otherwise register one per load,
  // every one an orphan the family's devices wrap keys for.
  const store = await probeKeyStore(forUser);
  if (store.state === 'blocked' || store.state === 'broken') {
    // Before D262 this path registered a key (the server row went in), then
    // failed to save it and threw: one orphan device key per page load,
    // and "locked until a key arrives" for ever. The error's name is all a
    // report may carry (D254).
    reportError({
      message: `e2ee: key store ${store.state} (${store.error ?? 'Error'}); no device key registered`,
    });
    setStorageState(store.state);
    return;
  }
  if (store.state === 'slow') {
    goSlow(forUser, 'probe');
    return;
  }
  setStorageState(store.state);
  // My own devices as the server listed them at start: the record learns
  // them once it is open (D300).
  let myDevices = null as Array<{
    id: string;
    publicKey: string;
    mlkemPublicKey?: string | null;
  }> | null;
  await withKeyLock(async () => {
    let stored: StoredDevice | null;
    try {
      // Only a `self` that is genuinely absent means "no key here": a read
      // that fails or times out must never lead to a fresh key overwriting
      // a good one (D262).
      stored = await loadDevice(forUser);
    } catch (err) {
      if (!isTimeout(err)) {
        reportError({
          message: `e2ee: stored device key could not be read (${(err as Error)?.name ?? 'Error'}); no device key registered`,
        });
      }
      goSlow(forUser, 'load');
      return;
    }
    if (stored) {
      let serverPublicKey: string | undefined;
      let lost = false;
      // A passkey removed elsewhere takes its device keys with it (D55).
      try {
        const mine = await api.getUserDeviceKeys(forUser);
        myDevices = mine.data.devices;
        const entry = mine.data.devices.find((row) => row.id === stored!.id);
        if (!entry) lost = true;
        else serverPublicKey = entry.publicKey;
      } catch {
        // Offline: keep using the stored key.
      }
      if (lost) {
        // Kept aside all the same, in case the list was wrong.
        try {
          await setAsideSelf(forUser, true);
        } catch (err) {
          reportError({
            message: `e2ee: lost device key could not be set aside (${(err as Error)?.name ?? 'Error'}); no device key registered`,
          });
          goSlow(forUser, 'set aside');
          return;
        }
        stored = null;
      } else {
        const verdict = await judgeKey(stored, serverPublicKey);
        if (verdict.action === 'repair') {
          stored = await repairPublicKey(forUser, stored, verdict.publicKey);
        } else if (verdict.action === 'unsupported') {
          setStorageState('unsupported');
        } else if (verdict.action === 'ambiguous') {
          setStorageState('unusable');
        } else if (verdict.action === 'replace') {
          stored = await replaceUnusableKey(
            forUser,
            stored,
            `${verdict.reason}, at start`,
            verdict.verified
          );
          if (!stored) return;
        }
      }
    }
    if (!stored) {
      if (
        store.state === 'ephemeral' &&
        readFlag('session', registeredInTab(forUser))
      ) {
        // This tab registered already and the key is gone again: the store
        // loses keys mid-visit. Another key would meet the same fate.
        setStorageState('blocked');
        return;
      }
      stored = await register(forUser);
    }
    if (userId === forUser) device = stored;
  });
  if (userId === forUser && device) {
    // A key registered just now starts a record with nothing to grandfather;
    // one kept from before M73 starts it as `legacy` (D304).
    await openTrust(forUser, !justRegistered);
    if (!myDevices && justRegistered) {
      // Registered just now: my devices as they are are a first contact,
      // so a later one is news (the server's D94 notice isn't relied on).
      try {
        myDevices = (await api.getUserDeviceKeys(forUser)).data.devices;
      } catch {
        // Offline: learned from the first keys answer instead.
      }
    }
    // This device's ML-KEM key (M78, D336): opened, or made and published;
    // in use once the server lists it for this device.
    const me = device;
    if (me) {
      const listed = myDevices;
      const row = (list: typeof listed, id: string) =>
        list
          ? (list.find((d) => d.id === id) ?? { mlkemPublicKey: null })
          : undefined;
      // One tab at a time makes, keeps and publishes it (point 3).
      const outcome = await (
        pqAvailable()
          ? withKeyLock(() => setUpMlkem(forUser, me, row(listed, me.id)))
          : Promise.resolve('ok' as const)
      ).catch(() => {
        countPqOnce('e2ee_pq_key_unavailable', 'setup');
        return 'ok' as const;
      });
      if (outcome === 'replace') {
        // The server holds an ML-KEM key for this device that this browser
        // can't use: a new device, as D262 replaces an unusable key; its
        // PQ state starts afresh (kept per device key). Re-checked under
        // the lock: another tab may have replaced it already.
        const fresh = await withKeyLock(async () =>
          (await loadDevice(forUser))?.id === me.id
            ? replaceUnusableKey(forUser, me, 'ML-KEM key lost', true, true)
            : null
        ).catch(() => null);
        if (fresh && userId === forUser) {
          device = fresh;
          // The old row goes now that the new one is saved (point 1).
          try {
            await api.deleteDeviceKey(me.id);
            await deletePreviousDevice(forUser, me.id);
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
              await deletePreviousDevice(forUser, me.id).catch(() => undefined);
            }
            // Otherwise `retirePreviousKey` deletes it next time.
          }
          try {
            myDevices = (await api.getUserDeviceKeys(forUser)).data.devices;
          } catch {
            myDevices = null;
          }
          await withKeyLock(() =>
            setUpMlkem(forUser, fresh, row(myDevices, fresh.id))
          ).catch(() => 'ok' as const);
        } else if (userId === forUser) {
          // Another tab replaced it: use what it kept.
          const now = await loadDevice(forUser).catch(() => null);
          if (now && now.id !== me.id) device = now;
        }
      }
    }
    if (myDevices) {
      await learnDevices(
        forUser,
        myDevices.map((d) => ({
          ...d,
          userId: forUser,
          // My own key is mine whatever the server says (§4.3's self-check
          // counts a difference).
          ...(d.id === device?.id ? { mlkemPublicKey: ownMlkem() } : {}),
        })),
        true,
        [forUser]
      );
    }
  }
  // A key replaced a week ago has had its chance to be undone.
  void retirePreviousKey(forUser);
}

/** A fresh device key, registered (which announces `keys:needed`, D57). */
async function register(forUser: string): Promise<StoredDevice> {
  const pair = await generateDeviceKeyPair();
  const publicKey = await exportPublicKey(pair.publicKey);
  // Its ML-KEM key, sealed before it is published and saved in the same
  // transaction as the P-256 key (M78, D336): a key the server lists but
  // this browser lost would lock every v2 copy for it.
  let pq: MlkemKeyPair | null = null;
  let pqSealed: StoredMlkem | null = null;
  try {
    if (!pqAvailable()) throw new Error('E2EE_MLKEM_OFF');
    pq = mlkemKeyPair();
    // A 201 to a registration that carried it is acceptance (final
    // review, point 2), as on the phones; ceilings are 0 everywhere.
    pqSealed = await sealMlkem(forUser, MLKEM_PENDING, pq, true, true);
  } catch {
    pq = null;
    if (pqAvailable()) countPqOnce('e2ee_pq_key_unavailable', 'register');
  }
  let response: Awaited<ReturnType<typeof api.registerDeviceKey>>;
  try {
    response = await api.registerDeviceKey(
      publicKey,
      deviceLabel(),
      pq?.publicKey
    );
  } catch (err) {
    // The server stops an account piling up device keys (D262): a browser
    // that lost every one so far would lose this one too.
    if (err instanceof ApiError && err.code === 'TOO_MANY_DEVICES') {
      setStorageState('tooMany');
    }
    throw err;
  }
  const stored = {
    id: response.data.id,
    privateKey: pair.privateKey,
    publicKey,
  };
  try {
    // In use once the server lists it for this device (`setUpMlkem`): a
    // server from before M78 would drop the field and answer 201 all the
    // same.
    await saveDevice(
      forUser,
      stored,
      pqSealed ? { ...pqSealed, deviceId: stored.id } : undefined
    );
  } catch (err) {
    // Unsaved, the key would be lost on reload and its row an orphan.
    void api.deleteDeviceKey(stored.id).catch(() => undefined);
    reportError({
      message: `e2ee: device key could not be saved (${(err as Error)?.name ?? 'Error'})`,
    });
    // A save that only timed out is the slow store, not a broken one: the
    // clear-site-data advice would delete the good key still in `self`.
    setStorageState(isTimeout(err) ? 'slow' : 'broken');
    throw err;
  }
  writeFlag('session', registeredInTab(forUser), stored.id);
  justRegistered = true;
  return stored;
}

/** Whose keys this module holds now (null signed out). */
export function e2eeUserId(): string | null {
  return userId;
}

/** Sets up (or switches to) this user's device key. */
export function startE2EE(forUser: string | null): Promise<void> {
  if (forUser === userId && ready) return ready;
  userId = forUser;
  device = null;
  contentKeys.clear();
  scopeInfo.clear();
  missingSince.clear();
  loading.clear();
  cancelAskRetries();
  justRegistered = false;
  lackedKeys = false;
  lastWakeAsk = 0;
  suspected.clear();
  slowRetried = false;
  previousPending = false;
  trust = null;
  trustInMemory = false;
  refused.clear();
  askedAfterRefusal.clear();
  membersReadAt.clear();
  membersLoading.clear();
  membersAsked.clear();
  membersForced.clear();
  revisionsShown.clear();
  revisionsIssued.clear();
  revisionsLoad = null;
  namesLoad = null;
  keptNames.clear();
  if (namesSaveTimer) clearTimeout(namesSaveTimer);
  namesSaveTimer = null;
  if (revisionsSaveTimer) clearTimeout(revisionsSaveTimer);
  revisionsSaveTimer = null;
  ceilings.clear();
  counted.clear();
  plainLooked.clear();
  mlkem = null;
  mlkemPublished = false;
  pqInvalid.clear();
  graceOverSeen = false;
  pqStates.clear();
  pqCurrentCopy.clear();
  pqFailed.clear();
  pqCounted.clear();
  downgradeCounted.clear();
  closeTrustChannel();
  noticeListeners.forEach((listener) => listener());
  if (slowTimer) clearTimeout(slowTimer);
  slowTimer = null;
  setStorageState('ok');
  ready = forUser
    ? setUp(forUser).catch((err) => {
        console.warn('[e2ee] device key setup failed', err);
      })
    : Promise.resolve();
  return ready;
}

/**
 * Logging out: the key leaves this device and the server (D55). After the
 * account is deleted the server has already dropped it (`server: false`).
 */
export async function forgetE2EE({ server = true }: { server?: boolean } = {}) {
  const forUser = userId;
  const current = device;
  startE2EE(null);
  if (current && server) {
    await api.deleteDeviceKey(current.id).catch(() => undefined);
  }
  if (forUser) {
    deleteDeviceStore(forUser);
    // The at-rest key went with the store (D281).
    forgetRestKey(forUser);
  }
}

export function deviceId(): string | null {
  return device?.id ?? null;
}

// ---- My record of devices (M73, D300–D304) ---------------------------------

// This device's record of everyone's devices (D300), mirrored from IndexedDB.
// Every check reads this copy; every change goes through `changeTrust`,
// which applies it to the stored copy in one transaction, and tells this
// user's other tabs to read it again.
let trust: TrustRecord | null = null;
// The record couldn't be opened: kept in memory for this visit only.
let trustInMemory = false;
// Wrapped keys refused this visit (D301), so each is counted once.
const refused = new Set<string>();
// Scopes whose refusal already asked for keys again (D57).
const askedAfterRefusal = new Set<string>();
const noticeListeners = new Set<() => void>();
// When this visit last read who is in each scope (D301).
const membersReadAt = new Map<string, number>();
const membersLoading = new Map<string, Promise<void>>();
// `<scope>|<userId>` → when someone not known as a member was last asked about.
const membersAsked = new Map<string, number>();
// The same, for reads forced by a key about to be wrapped.
const membersForced = new Map<string, number>();
// A listed user nobody knows asks for the members again at most this often.
export const MEMBERS_AGAIN_MS = 30_000;
let trustChannel: BroadcastChannel | null = null;

const notifyNotices = () => noticeListeners.forEach((listener) => listener());

function closeTrustChannel() {
  try {
    trustChannel?.close();
  } catch {
    // Already closed.
  }
  trustChannel = null;
}

/** Another tab changed the record: read it again (live notices too). */
function openTrustChannel(forUser: string) {
  closeTrustChannel();
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(`twinden-trust-${forUser}`);
    channel.onmessage = () => {
      if (userId !== forUser || trustInMemory) return;
      void loadTrustRecord<unknown>(forUser)
        .then((stored) => {
          const fresh = normalizeTrustRecord(stored);
          if (fresh && userId === forUser) {
            trust = fresh;
            notifyNotices();
          }
        })
        .catch(() => undefined);
    };
    // Never keeps a process alive on its own (tests, workers).
    (channel as { unref?: () => void }).unref?.();
    trustChannel = channel;
  } catch {
    // No channel: other tabs catch up on their next load.
  }
}

/**
 * Opens (or starts) this user's record (D300). An empty store is the first
 * run after this update or a new sign-in: started here, and filled silently
 * from what the server says (D304).
 */
async function openTrust(forUser: string, legacy: boolean) {
  let record: TrustRecord;
  try {
    const stored = normalizeTrustRecord(await loadTrustRecord(forUser));
    record =
      stored ??
      (await updateTrustRecord<TrustRecord>(
        forUser,
        (current) =>
          normalizeTrustRecord(current) ?? newTrustRecord(Date.now(), legacy)
      ));
    trustInMemory = false;
  } catch (err) {
    // The key itself was read from this store a moment ago, so this is a
    // hiccup: a record for this visit only, trusting what the server says
    // (as on a first run) rather than locking every chat. Counted (D254).
    reportError({
      message: `e2ee: device record could not be opened (${(err as Error)?.name ?? 'Error'}); kept in memory`,
    });
    countClientFailure('e2ee_record_unavailable');
    record = newTrustRecord(Date.now(), true);
    trustInMemory = true;
  }
  if (userId !== forUser) return;
  trust = record;
  if (!trustInMemory) openTrustChannel(forUser);
  notifyNotices();
}

/** Applies a change to the record, here and in the store (D300). */
async function changeTrust(
  forUser: string,
  change: (record: TrustRecord) => TrustRecord
) {
  if (!trust || userId !== forUser) return;
  // Nothing new: no write (most keys answers repeat what's recorded).
  const before = trust;
  if (JSON.stringify(change(before)) === JSON.stringify(before)) return;
  let next: TrustRecord;
  let stored = false;
  if (trustInMemory) {
    next = change(before);
  } else {
    try {
      // Applied to the stored copy, which another tab may have moved on.
      next = await updateTrustRecord<TrustRecord>(forUser, (current) =>
        change(normalizeTrustRecord(current) ?? before)
      );
      stored = true;
    } catch {
      // Not kept this time; this visit still uses it.
      next = change(trust ?? before);
    }
  }
  if (userId !== forUser) return;
  trust = next;
  // Someone joined a chat or family (D343): the lines show at once.
  if (next.joined !== before.joined) notifyNotices();
  if (stored) {
    try {
      trustChannel?.postMessage('changed');
    } catch {
      // Other tabs catch up on their next load.
    }
  }
}

/**
 * Learns the devices a keys answer lists (D300), before anything is checked
 * or wrapped against them (D301, D302). A known person's new device puts the
 * "devices changed" line in our chats (D303); a new device of mine, other
 * than this browser's own key, the notice about my account.
 */
async function learnDevices(
  forUser: string,
  listed: readonly ListedDevice[],
  complete: boolean,
  completeFor: readonly string[] = []
) {
  let changed = false;
  let notes: Array<{
    count: 'e2ee_pq_key_changed' | 'e2ee_pq_key_missing';
    publicKey: string;
  }> = [];
  const own = device?.publicKey;
  await changeTrust(forUser, (record) => {
    const merged = mergeDevices(
      record,
      forUser,
      listed,
      Date.now(),
      complete,
      own,
      completeFor
    );
    changed = merged.gained.length > 0 || merged.mine;
    notes = merged.pqNotes;
    return merged.record;
  });
  // Another ML-KEM key, or none, for a recorded one (D338): the recorded
  // key stays; counted once per device per launch.
  for (const note of notes) countPqOnce(note.count, note.publicKey);
  if (changed) notifyNotices();
}

/**
 * Who is in a chat or family now, as the app shows it (D301): the
 * participants or members, never the owners a keys answer lists. Read once
 * a visit (or taken from the lists the app loaded, `noteScopeMembers`), and
 * again when an answer lists someone not known, at most every
 * MEMBERS_AGAIN_MS. Anyone still unknown is deferred: not learned, not
 * accepted from, not wrapped for. Offline, the members last recorded.
 */
async function scopeMembers(
  forUser: string,
  kind: ScopeKind,
  id: string,
  listedUsers: readonly string[],
  /** Read now for anyone unknown, throttle or not: a key is to be wrapped. */
  force = false
): Promise<{ members: Set<string>; known: boolean; fresh: boolean }> {
  const scope = `${kind}:${id}`;
  const recorded = () =>
    trust ? currentMembers(trust, scope) : new Set<string>();
  const now = Date.now();
  // Throttled per unknown person, not per scope: someone the server made up
  // is asked about at most every MEMBERS_AGAIN_MS, but a real member added
  // a moment ago is asked about at once.
  const unknown = listedUsers.filter(
    (user) => user !== forUser && !recorded().has(user)
  );
  const due = unknown.filter(
    (user) =>
      now - (membersAsked.get(`${scope}|${user}`) ?? -Infinity) >=
      MEMBERS_AGAIN_MS
  );
  // Forced (a key is to be wrapped for them): once per person at once, then
  // the same 30 s per person, so a stranger who stays in every answer costs
  // one read, not one per send.
  const forced = force
    ? unknown.filter(
        (user) =>
          now - (membersForced.get(`${scope}|${user}`) ?? -Infinity) >=
          MEMBERS_AGAIN_MS
      )
    : [];
  if (!membersReadAt.has(scope) || due.length > 0 || forced.length > 0) {
    for (const user of due) membersAsked.set(`${scope}|${user}`, now);
    for (const user of forced) membersForced.set(`${scope}|${user}`, now);
    let running = membersLoading.get(scope);
    if (!running) {
      running = (async () => {
        try {
          const ids = await api.getScopeMemberIds(kind, id);
          membersReadAt.set(scope, Date.now());
          await changeTrust(forUser, (record) =>
            noteMembers(record, scope, ids, Date.now())
          );
        } catch {
          // Offline or refused: the members last recorded.
        }
      })().finally(() => membersLoading.delete(scope));
      membersLoading.set(scope, running);
    }
    await running;
  }
  const members = recorded();
  const known = members.size > 0;
  members.add(forUser);
  return { members, known, fresh: membersReadAt.has(scope) };
}

/**
 * The participants of chats, or the members of a family, as a list the app
 * loaded shows them (M73, D301): recorded as who is in each, so the keys
 * check needn't ask again this visit.
 */
export async function noteScopeMembers(
  entries: ReadonlyArray<{ kind: ScopeKind; id: string; userIds: string[] }>
) {
  await ready;
  const forUser = userId;
  if (!forUser || !trust || entries.length === 0) return;
  const now = Date.now();
  for (const entry of entries)
    membersReadAt.set(`${entry.kind}:${entry.id}`, now);
  await changeTrust(forUser, (record) =>
    entries.reduce(
      (next, entry) =>
        noteMembers(next, `${entry.kind}:${entry.id}`, entry.userIds, now),
      record
    )
  );
}

/**
 * A family's chat list as the app loaded it (M73): its participants are
 * who is in each chat (D301), and on a device from before M73, the family's
 * first chat list names the chats that may have a first look (D304).
 */
export async function noteChatList(
  familyId: string,
  chats: ReadonlyArray<{ id: string; userIds: string[] }>
) {
  await ready;
  // The family list names the families first (D304).
  await familyListNoted;
  const forUser = userId;
  if (!forUser || !trust) return;
  await applyChatList(forUser, familyId, chats);
}

async function applyChatList(
  forUser: string,
  familyId: string,
  chats: ReadonlyArray<{ id: string; userIds: string[] }>
) {
  const now = Date.now();
  for (const chat of chats) membersReadAt.set(`chat:${chat.id}`, now);
  await changeTrust(forUser, (record) =>
    noteChatListFor(
      chats.reduce(
        (next, chat) => noteMembers(next, `chat:${chat.id}`, chat.userIds, now),
        record
      ),
      familyId,
      chats.map((chat) => chat.id)
    )
  );
}

/**
 * The family list as the app loaded it (M73): on a device from before M73,
 * the first one names the families that may have a first look (D304).
 */
export function noteFamilyList(familyIds: readonly string[]): Promise<void> {
  familyListNoted = (async () => {
    await ready;
    const forUser = userId;
    if (!forUser || !trust) return;
    const before = trust.lookFamilies;
    await changeTrust(forUser, (record) =>
      noteFamilyListFor(record, familyIds)
    );
    // The first list just named the families (D304): take each one's chat
    // list now, not only the ones opened, so the first-look lists are
    // complete. Ids only, without opening anything.
    if (before === undefined && trust?.lookFamilies !== undefined) {
      for (const familyId of trust.lookFamilies) {
        try {
          const chats = await api.getChatMemberLists(familyId);
          await applyChatList(forUser, familyId, chats);
        } catch {
          // Offline: the chat list the app loads later does it.
        }
      }
    }
  })().catch(() => undefined);
  return familyListNoted;
}
let familyListNoted: Promise<void> = Promise.resolve();

/**
 * When this device last noticed each person gain a device (D303), userId →
 * ms. Local only: never sent, never unread, not a message.
 */
export function deviceNotices(): Readonly<Record<string, number>> {
  // One empty object, so a React snapshot stays the same while signed out.
  return trust?.notices ?? NO_NOTICES;
}
const NO_NOTICES: Readonly<Record<string, number>> = Object.freeze({});

/**
 * Who joined each chat or family after this device's first look at it, and
 * when this device noticed (D343): `<kind>:<id>` → userId → ms. Local only:
 * the "{Name} was added to this chat" lines, never sent or unread.
 */
export function memberJoins(): Readonly<
  Record<string, Readonly<Record<string, number>>>
> {
  return trust?.joined ?? NO_JOINS;
}
const NO_JOINS: Readonly<Record<string, Readonly<Record<string, number>>>> =
  Object.freeze({});

/**
 * When this device noticed a new device on my own account that it hasn't
 * shown yet (M73): the server's D94 notice can't be relied on to say so.
 */
export function ownDeviceNotice(): number | null {
  return trust && userId ? ownNotice(trust, userId) : null;
}

/** The notice about my own devices was shown: not again for this one. */
export async function ackOwnDeviceNotice() {
  const forUser = userId;
  const at = forUser && trust ? ownNotice(trust, forUser) : null;
  if (!forUser || at === null) return;
  await changeTrust(forUser, (record) => ({ ...record, ownSeenAt: at }));
  notifyNotices();
}

/** Runs `listener` when `deviceNotices()` may have changed. */
export function onDeviceNotices(listener: () => void): () => void {
  noticeListeners.add(listener);
  return () => {
    noticeListeners.delete(listener);
  };
}

/**
 * A person's security code (D63) from this device's record (D302): their
 * devices are asked for and learned first (a new one is announced, D303),
 * and the code is computed from the recorded current keys, so it describes
 * the devices this device would wrap for. Same keys, same code as before
 * M73. Without a device key here, the server's list as before.
 */
export async function securityCodeFor(person: string): Promise<string | null> {
  return (await securityCodeDetailsFor(person)).code;
}

/**
 * A person's security code (D63, D338) with what the codes screen says
 * beside it: each device's line is its P-256 key, or `<P-256> <ML-KEM>`
 * once it has one. `allPq`: every device of theirs has an ML-KEM key (an
 * older app shows a different code for them until then). `needsUpdate`:
 * how many of their devices the server marks as needing an update after
 * the grace (§6.5).
 */
export async function securityCodeDetailsFor(person: string): Promise<{
  code: string | null;
  allPq: boolean;
  needsUpdate: number;
}> {
  await ready;
  const forUser = userId;
  const { data } = await api.getUserDeviceKeys(person);
  noteGraceAnswer(data.pqGraceOver);
  const listed = data.devices.map((d) => ({
    ...d,
    userId: person,
    mlkemPublicKey:
      person === forUser && d.id === device?.id
        ? ownMlkem()
        : checkedMlkem(d.mlkemPublicKey, d.publicKey),
  }));
  if (forUser && device && trust) {
    await learnDevices(forUser, listed, true, [person]);
  }
  const devices =
    trust && forUser && userId === forUser
      ? activeDevices(trust, person)
      : listed.map((d) => ({
          publicKey: d.publicKey,
          mlkem: d.mlkemPublicKey ?? null,
        }));
  return {
    code:
      devices.length > 0
        ? await securityCode(devices.map(securityCodeLine))
        : null,
    allPq: devices.every((d) => d.mlkem !== null),
    needsUpdate: data.devices.filter((d) => d.needsUpdate === true).length,
  };
}

// ---- Chat and family keys ---------------------------------------------------

async function fetchScope(
  kind: ScopeKind,
  id: string
): Promise<ScopeKeys | null> {
  await ready;
  if (!device) return null;
  const loadingId = `${kind}:${id}`;
  const running = loading.get(loadingId);
  if (running) return running;
  const me = device;
  const owner = userId;
  const request = (async () => {
    try {
      let data: ScopeKeys;
      let fromCache = false;
      try {
        data = (await api.getScopeKeys(kind, id, me.id)).data;
        // The post-quantum grace is over (M78, §6.5): devices without an
        // ML-KEM key get nothing from now on; this one shows the update
        // banner only if its own key isn't accepted. Reading goes on.
        noteGraceAnswer(data.pqGraceOver);
        // An answer below the epoch this device has seen isn't kept for
        // sealing offline: nothing stored goes back (D341).
        const rolledBack =
          !!trust &&
          checkSendEpoch(trust, loadingId, data.currentEpoch) === 'rollback';
        if (owner && !rolledBack) {
          void saveScopeInfo(owner, loadingId, data).catch(() => {});
        }
      } catch (err) {
        // Offline: seal with what this device last knew (M29, D67).
        const offline = !(err instanceof ApiError) || err.status === 0;
        const cached =
          offline && owner
            ? await loadScopeInfo<ScopeKeys>(owner, loadingId).catch(() => null)
            : null;
        if (!cached) throw err;
        data = cached;
        fromCache = true;
      }
      // ML-KEM keys failing FIPS 203's check count as missing (§4.2); my
      // own device's is the key I hold.
      data = {
        ...data,
        devices: data.devices.map((d) => ({
          ...d,
          mlkemPublicKey:
            d.id === me.id
              ? ownMlkem()
              : checkedMlkem(d.mlkemPublicKey, d.publicKey),
        })),
      };
      scopeInfo.set(loadingId, data);
      // This scope's PQ ceiling (D339), written at acceptance (M4).
      await pqStateFor(loadingId);
      // The v1 ceiling follows fresh answers only (D328).
      if (!fromCache) {
        noteFreshAnswer(
          loadingId,
          data.currentEpoch,
          typeof data.v2SinceEpoch === 'number' ? data.v2SinceEpoch : null
        );
      }
      // When its encryption began, the first time this device sees it
      // (D347): plain text from then on is refused. Never moved later.
      const since = data.encryptedSince ? Date.parse(data.encryptedSince) : NaN;
      if (owner && Number.isFinite(since)) {
        await changeTrust(owner, (record) =>
          notePlainBefore(record, loadingId, since, 'server')
        );
      }
      // Who is in it, as the app shows it (D301): a keys answer's device
      // owners alone could include someone the server made up. Then read
      // and record those members' devices first, and check after: a
      // contact's new device that wrapped a key for me is listed in this
      // same answer. An offline copy only adds — it may predate a device
      // that came since.
      const listedUsers = [...new Set(data.devices.map((d) => d.userId))];
      const { members, known, fresh } = owner
        ? await scopeMembers(owner, kind, id, listedUsers)
        : { members: new Set<string>(), known: false, fresh: false };
      if (owner) {
        // Members with no device listed lose their keys only when the
        // members were read this visit: a stale list would remove the keys
        // of someone who in fact left (D301).
        await learnDevices(
          owner,
          data.devices.filter((d) => members.has(d.userId)),
          !fromCache,
          known && fresh ? [...members] : []
        );
      }
      // When each epoch began (M73): absent from an older server's answer,
      // and treated as absent when the times make no sense (going down as
      // epochs go up, or in the future). They only ever narrow the first
      // look; nothing else rests on them.
      const parsed = (data.epochs ?? []).map((e) => ({
        epoch: e.epoch,
        createdAt: Date.parse(e.createdAt),
      }));
      const epochsKnown =
        Array.isArray(data.epochs) && epochTimesSane(parsed, Date.now());
      const epochAt = new Map(
        epochsKnown ? parsed.map((e) => [e.epoch, e.createdAt]) : []
      );
      const pins: Array<{ epoch: number; wrapper: string }> = [];
      let unopenable = 0;
      let attempted = 0;
      let opened = 0;
      for (const entry of data.keys) {
        const cacheId = keyId(kind, id, entry.epoch);
        const createdAt = epochAt.get(entry.epoch);
        // Told by length and first byte (D337); anything else never opens.
        const format = wrappedKeyFormat(entry.wrappedKey);
        if (format === 'invalid') {
          // Never opens; counted as before M78 (D262's check of this
          // device's own key looks at copies that won't open).
          attempted += 1;
          unopenable += 1;
          continue;
        }
        if (entry.epoch === data.currentEpoch) {
          pqCurrentCopy.set(loadingId, format);
        }
        const check = (record: TrustRecord) =>
          checkWrapper(record, {
            scope: loadingId,
            epoch: entry.epoch,
            epochCreatedAt:
              createdAt !== undefined && Number.isFinite(createdAt)
                ? createdAt
                : null,
            wrapper: entry.wrappedByPublicKey,
            members: [...members],
            me: owner!,
            ownPublicKey: me.publicKey,
          });
        if (contentKeys.has(cacheId)) {
          // Opened before M73 pinned anything, or by a tab: pin it now.
          if (trust && owner && check(trust) !== 'refused') {
            pins.push({
              epoch: entry.epoch,
              wrapper: entry.wrappedByPublicKey,
            });
          }
          continue;
        }
        let verdict = trust && owner ? check(trust) : 'refused';
        if (
          (verdict === 'firstLook' ||
            verdict === 'refused' ||
            verdict === 'lapsed') &&
          owner &&
          !trustInMemory
        ) {
          // Another tab may have used up this scope's first look, or learned
          // what this copy needs: decide from the stored record, not this
          // tab's copy, before accepting a first look or refusing (D304).
          const stored = normalizeTrustRecord(
            await loadTrustRecord(owner).catch(() => null)
          );
          if (stored && userId === owner) {
            trust = stored;
            verdict = check(stored);
          }
        }
        if (verdict === 'refused' || verdict === 'lapsed') {
          // Not from anyone in this chat or family (now, or for an epoch this
          // device opened before they left), nor from my own devices:
          // treated as not arrived (D301). Counted once, with no
          // identifiers (D254), unless who is in it couldn't be read at all;
          // a lapsed one apart — usually an honest lockout (a device that
          // never opened the scope before the sharer left); and keys are
          // asked for again (D57).
          if (!refused.has(cacheId) && known) {
            refused.add(cacheId);
            countClientFailure(
              verdict === 'lapsed'
                ? 'e2ee_wrapper_lapsed'
                : 'e2ee_wrapper_refused'
            );
          }
          if (!askedAfterRefusal.has(loadingId)) {
            askedAfterRefusal.add(loadingId);
            void askForKeys();
          }
          continue;
        }
        // A v1 copy from a wrapper the record shows with an ML-KEM key,
        // above this device's PQ ceiling, is a downgrade (D339, §6.2):
        // refused like an unknown wrapper, keys asked for again once.
        if (
          format === 'v1' &&
          trust &&
          v1CopyDecision({
            iHaveMlkem: iHaveMlkem(),
            wrapperHasMlkem: wrapperHasMlkem(trust, entry.wrappedByPublicKey),
            epoch: entry.epoch,
            ceiling: pqStates.get(pqStateKey(loadingId))?.ceiling ?? null,
          }) === 'refuse'
        ) {
          countPqOnce('e2ee_pq_downgrade', cacheId);
          if (!askedAfterRefusal.has(loadingId)) {
            askedAfterRefusal.add(loadingId);
            void askForKeys();
          }
          continue;
        }
        if (format === 'v2') {
          // Opened with both of this device's keys (§5); a failure is a
          // locked chat and a rotation, never a wrong key (§4.3), and says
          // nothing about the P-256 key alone.
          try {
            if (!mlkem) throw new Error('E2EE_MLKEM_UNAVAILABLE');
            contentKeys.set(
              cacheId,
              await unwrapContentKeyV2({
                wrapped: entry.wrappedKey,
                myPrivate: me.privateKey,
                mySpki: me.publicKey,
                myMlkem: mlkem,
                wrapperSpki: entry.wrappedByPublicKey,
                scope: { kind, scopeId: id, epoch: entry.epoch },
              })
            );
            missingSince.delete(cacheId);
            opened += 1;
            pins.push({
              epoch: entry.epoch,
              wrapper: entry.wrappedByPublicKey,
            });
          } catch {
            countPqOnce('e2ee_pq_unwrap_failed', cacheId);
            pqFailed.add(loadingId);
          }
          continue;
        }
        attempted += 1;
        try {
          contentKeys.set(
            cacheId,
            await unwrapContentKey(
              entry.wrappedKey,
              me.privateKey,
              entry.wrappedByPublicKey,
              wrapScope(kind, id, entry.epoch)
            )
          );
          missingSince.delete(cacheId);
          opened += 1;
          // Every wrapper that opened stays accepted for its epoch, whoever
          // leaves or signs out later (D301).
          pins.push({ epoch: entry.epoch, wrapper: entry.wrappedByPublicKey });
        } catch {
          // A copy this device can't open; another device may share a good one.
          unopenable += 1;
        }
      }
      // Pin what opened; raise the epoch watermark from what opened (or is
      // held), never from the epochs the answer claims (D301, D58); and a
      // pre-M73 device's first look at a listed scope is over once an answer
      // said when its epochs began (D304).
      if (owner) {
        const openedAt = Date.now();
        await changeTrust(owner, (record) =>
          raiseWatermark(
            afterLook(record, loadingId, pins, epochsKnown && record.legacy),
            loadingId,
            pins.map((pin) => pin.epoch),
            openedAt
          )
        );
      }
      // Every copy wrapped for this device failed: perhaps this device's
      // own key is the problem (D262). Checked once per key. A refused copy
      // says nothing about this device's key.
      if (unopenable > 0 && unopenable === attempted && owner) {
        void suspectUnusableKey(owner, me);
      }
      // A copy shared for the fresh key opened: it works, so the key it
      // replaced has had its chance to be undone (D262).
      if (opened > 0 && previousPending && owner && device?.id === me.id) {
        previousPending = false;
        void retirePreviousKey(owner, true);
      }
      return data;
    } catch {
      return null;
    } finally {
      loading.delete(loadingId);
    }
  })();
  loading.set(loadingId, request);
  return request;
}

/** An opened chat or family key, or null when this device hasn't got it. */
export async function keyFor(
  kind: ScopeKind,
  id: string,
  epoch: number
): Promise<CryptoKey | null> {
  const cacheId = keyId(kind, id, epoch);
  const cached = contentKeys.get(cacheId);
  if (cached) return cached;
  const missing = missingSince.get(cacheId);
  if (missing && Date.now() - missing < 15_000) return null;
  await fetchScope(kind, id);
  const key = contentKeys.get(cacheId) ?? null;
  if (!key) missingSince.set(cacheId, Date.now());
  return key;
}

/**
 * The key to write with: the current one, or a new epoch when there is none
 * yet, someone left (D58), or this device hasn't been given the current one
 * (a new device can always start a fresh epoch for everyone).
 */
async function sendingKey(
  kind: ScopeKind,
  id: string
): Promise<{ epoch: number; key: CryptoKey }> {
  const scope = `${kind}:${id}`;
  let askedAgain = false;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const known = attempt === 0 ? scopeInfo.get(scope) : undefined;
    let info = known ?? (await fetchScope(kind, id));
    if (!info || !device)
      throw new Error('End-to-end encryption isn’t ready on this device');
    // Never under an epoch older than one this device has opened or made
    // here (D341): a server could name one from before someone was removed,
    // who still holds its key. Like STALE_KEY, the keys are asked for again;
    // an answer still below it sends nothing.
    if (
      trust &&
      checkSendEpoch(trust, scope, info.currentEpoch) === 'rollback'
    ) {
      forgetScope(kind, id);
      if (!askedAgain) {
        askedAgain = true;
        continue;
      }
      if (!rolledBackScopes.has(scope)) {
        rolledBackScopes.add(scope);
        countClientFailure('e2ee_epoch_rollback');
      }
      throw new EpochRollbackError();
    }
    // The answer lists someone not (yet) known as a member: perhaps just
    // added. Their devices are wrapped for only once they're known, so ask
    // now, before a new epoch would leave them out (D301).
    info = await withNewMembers(kind, id, info);
    const current = info.currentEpoch;
    const have =
      current > 0 ? contentKeys.get(keyId(kind, id, current)) : undefined;
    // Once per scope, a device with an ML-KEM key whose own copy of the
    // current key is v1 (or whose v2 copy didn't open) starts a new epoch,
    // whether or not the server asked (D339, §6.3).
    const pqRotate = pqRotationDue(scope);
    if (have && !info.needsRotation && !pqRotate) {
      return { epoch: current, key: have };
    }

    const epoch = current + 1;
    const key = await generateContentKey();
    // Only devices in my record (D302): `fetchScope` recorded every device
    // this answer lists — announcing a known person's new one (D303) —
    // before anything is wrapped for it.
    const record = trust;
    const me = userId;
    const inScope = record ? currentMembers(record, `${kind}:${id}`) : null;
    // Who is in it couldn't be read: a new epoch now would leave everyone
    // else out of it.
    if (inScope && inScope.size === 0) {
      throw new Error('End-to-end encryption isn’t ready on this device');
    }
    // ...and only for people in it as the app shows it (D301): a device
    // under someone the server made up gets nothing.
    const targets =
      record && inScope
        ? info.devices.filter(
            (target) =>
              (target.userId === me || inScope.has(target.userId)) &&
              isRecorded(record, target.userId, target.publicKey)
          )
        : info.devices;
    // v2 for every device the record has an ML-KEM key for, v1 for the
    // others during the grace, none after it (D339).
    const keys: Array<{ deviceKeyId: string; wrappedKey: string }> = [];
    for (const [i, target] of targets.entries()) {
      if (i > 0 && i % 16 === 0) await breathe();
      const wrappedKey = await wrapFor(key, target, kind, id, epoch);
      if (wrappedKey) keys.push({ deviceKeyId: target.id, wrappedKey });
    }
    if (keys.length === 0) {
      throw new Error('End-to-end encryption isn’t ready on this device');
    }
    try {
      await api.createKeyEpoch(kind, id, {
        epoch,
        wrappedByDeviceId: device.id,
        keys,
      });
      contentKeys.set(keyId(kind, id, epoch), key);
      const mine = keys.find((k) => k.deviceKeyId === device!.id);
      if (mine) {
        const format = wrappedKeyFormat(mine.wrappedKey);
        if (format !== 'invalid') pqCurrentCopy.set(scope, format);
      }
      if (pqRotate) {
        pqFailed.delete(scope);
        await changePqState(scope, (s) => ({ ...s, rotated: true }));
      }
      // An epoch this device made is one it has seen: nothing is sealed
      // under an older one from now on (D341).
      if (userId) {
        const owner = userId;
        await changeTrust(owner, (record) =>
          raiseWatermark(record, scope, [epoch], Date.now())
        );
      }
      // The next message seals with this epoch straight away.
      scopeInfo.set(`${kind}:${id}`, {
        ...info,
        currentEpoch: epoch,
        needsRotation: false,
      });
      // Refresh what's kept for sealing offline, so it knows this epoch (D67).
      void fetchScope(kind, id);
      return { epoch, key };
    } catch (err) {
      // Someone else started this epoch first: use theirs.
      if (!(err instanceof ApiError && err.code === 'EPOCH_EXISTS')) throw err;
    }
  }
  throw new Error('Could not agree on a key');
}

/**
 * The server named an epoch older than one this device has opened or made
 * in that chat or family, twice (D341): nothing is sealed under it. The send
 * fails like any other and can be tried again.
 */
export class EpochRollbackError extends Error {
  constructor() {
    super('End-to-end encryption isn’t ready on this device');
    this.name = 'EpochRollbackError';
  }
}

// Scopes whose rollback was counted this visit (D341, D254): once each.
const rolledBackScopes = new Set<string>();

/**
 * A chat's or family's current key epoch, asked of the server now (another
 * device may have rotated it, and nothing says so). null when it can't be
 * had now, or this device hasn't been given that key yet: a write then
 * would start a new epoch for everyone, which background work (sealing old
 * pictures, wrapping picture keys, D295, D297) mustn't do. 0, a scope with
 * no key yet, may start the first. Never starts an epoch itself.
 */
export async function scopeKeyEpoch(
  kind: ScopeKind,
  id: string
): Promise<number | null> {
  try {
    const info = await fetchScope(kind, id);
    if (!info) return null;
    const current = info.currentEpoch;
    if (current > 0 && !contentKeys.has(keyId(kind, id, current))) return null;
    // Older than what this device has seen (D341): not the key to write with.
    if (
      trust &&
      checkSendEpoch(trust, `${kind}:${id}`, current) === 'rollback'
    ) {
      return null;
    }
    return current;
  } catch {
    return null;
  }
}

/** A family's current key epoch, if this device holds it (see above). */
export function familyKeyEpoch(familyId: string): Promise<number | null> {
  return scopeKeyEpoch('family', familyId);
}

/** Drops what this device knew about a scope's keys (after STALE_KEY etc.). */
export function forgetScope(kind: ScopeKind, id: string) {
  scopeInfo.delete(`${kind}:${id}`);
}

/**
 * Forgets a chat's or family's keys on this device for good (M54, D191):
 * the opened keys, what was known for sealing, and the saved copy of it.
 * Used when the family is deleted, so nothing of it can be opened here.
 */
export function forgetScopeKeys(kind: ScopeKind, id: string) {
  const prefix = `${kind}:${id}:`;
  for (const cacheId of [...contentKeys.keys()]) {
    if (cacheId.startsWith(prefix)) contentKeys.delete(cacheId);
  }
  for (const cacheId of [...missingSince.keys()]) {
    if (cacheId.startsWith(prefix)) missingSince.delete(cacheId);
  }
  scopeInfo.delete(`${kind}:${id}`);
  loading.delete(`${kind}:${id}`);
  if (userId) void deleteScopeInfo(userId, `${kind}:${id}`).catch(() => {});
}

export function isE2EEReady(): boolean {
  return device !== null;
}

// Device keys already checked after copies for them failed to open.
const suspected = new Set<string>();

/**
 * Keys wrapped for this device wouldn't open (D262). The replacement rule
 * (`judgeKey`) decides; a fresh key asks for everything again (D57), and
 * the opened and remembered keys of the old one are dropped.
 */
async function suspectUnusableKey(forUser: string, me: StoredDevice) {
  if (suspected.has(me.id)) return;
  suspected.add(me.id);
  let serverPublicKey: string | undefined;
  try {
    const mine = await api.getUserDeviceKeys(forUser);
    serverPublicKey = mine.data.devices.find(
      (row) => row.id === me.id
    )?.publicKey;
  } catch {
    // Offline: no verdict without the server's key; the next load checks.
  }
  const verdict = await judgeKey(me, serverPublicKey);
  if (userId !== forUser || device?.id !== me.id) return;
  if (verdict.action === 'keep') return;
  if (verdict.action === 'unsupported') {
    setStorageState('unsupported');
    return;
  }
  if (verdict.action === 'ambiguous') {
    setStorageState('unusable');
    return;
  }
  if (verdict.action === 'repair') {
    const repaired = await repairPublicKey(forUser, me, verdict.publicKey);
    if (userId === forUser && device?.id === me.id) {
      device = repaired;
      suspected.delete(me.id);
      missingSince.clear();
      loading.clear();
      arrivedListeners.forEach((listener) => listener());
    }
    return;
  }
  let adopted: StoredDevice | null = null;
  const fresh = await withKeyLock(async () => {
    // Another tab may have replaced it while this one waited for the lock:
    // use the key it stored rather than keep the retired one.
    const current = await loadDevice(forUser);
    if (current?.id !== me.id) {
      adopted = current;
      return null;
    }
    return replaceUnusableKey(
      forUser,
      me,
      `${verdict.reason}, wrapped keys would not open`,
      verdict.verified
    );
  }).catch(() => null);
  if (userId !== forUser || device?.id !== me.id) return;
  const next = fresh ?? adopted;
  if (!next) return;
  device = next;
  contentKeys.clear();
  scopeInfo.clear();
  missingSince.clear();
  loading.clear();
}

/**
 * What is sealed, besides its chat and client id (M76, D325): a message of
 * this type at this revision (0 when sent, D326), or a group's description
 * or picture.
 */
export type SealAs =
  | { type: string; revision?: number }
  | { group: GroupBinding['which']; revision: number };

/** A sealed message or group info, ready to send (v2, D324). */
export interface SealedOut {
  content: string;
  keyEpoch: number;
  encryption: 'v2';
}

/** Seals an envelope for a chat (D59, D325): what to send. */
export async function seal(
  conversationId: string,
  clientId: string,
  envelope: Envelope,
  as: SealAs
): Promise<SealedOut> {
  await ready;
  const sender = userId;
  if (!sender)
    throw new Error('End-to-end encryption isn’t ready on this device');
  const { epoch, key } = await sendingKey('chat', conversationId);
  const aad =
    'group' in as
      ? groupAadV2({
          conversationId,
          clientId,
          which: as.group,
          epoch,
          revision: as.revision,
        })
      : messageAadV2({
          conversationId,
          senderId: sender,
          clientId,
          type: as.type,
          epoch,
          revision: as.revision ?? 0,
        });
  return {
    content: await sealEnvelope(key, envelope, aad),
    keyEpoch: epoch,
    encryption: 'v2',
  };
}

/**
 * Sends something sealed, sealing again once if the server says the chat has
 * moved to a newer key in the meantime.
 */
export async function sendSealed<T>(
  conversationId: string,
  clientId: string,
  envelope: Envelope,
  as: SealAs,
  send: (sealed: SealedOut) => Promise<T>
): Promise<T> {
  try {
    return await send(await seal(conversationId, clientId, envelope, as));
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.code === 'STALE_KEY' || err.code === 'ROTATION_REQUIRED')
    ) {
      forgetScope('chat', conversationId);
      return send(await seal(conversationId, clientId, envelope, as));
    }
    throw err;
  }
}

// ---- Revisions and v1 ceilings (M76, D326, D328) ---------------------------

// The highest revision opened per message (keyed by chat, sender and client
// id) and per group description or picture (chat, `group`, which), kept
// sealed at rest in the device store (D281) so a reload can't be shown an
// older edit (D326 c); and the highest this device has sealed an edit for,
// so a queued edit counts.
const revisionsShown = new Map<string, { r: number; at: number }>();
const revisionsIssued = new Map<string, number>();
const revisionKey = (
  conversationId: string,
  senderId: string,
  clientId: string
) => `${conversationId}:${senderId}:${clientId}`.toLowerCase();
const groupRevisionKey = (
  conversationId: string,
  which: GroupBinding['which']
) => `${conversationId}:group:${which}`.toLowerCase();

const REVISIONS_SCOPE = 'm76-revisions';
// Kept 30 days after last seen, at most 10 000 (the edit window is far
// shorter; this outlasts it for what was edited near its end).
const REVISION_KEEP_MS = 30 * 24 * 60 * 60 * 1000;
const REVISION_MAX = 10_000;
let revisionsLoad: Promise<void> | null = null;
let revisionsSaveTimer: ReturnType<typeof setTimeout> | null = null;

function loadRevisions(forUser: string): Promise<void> {
  revisionsLoad ??= (async () => {
    try {
      const sealed = await loadScopeInfo<string>(forUser, REVISIONS_SCOPE);
      const text = sealed ? await openText(forUser, sealed) : null;
      if (!text || userId !== forUser) return;
      const now = Date.now();
      for (const [key, value] of Object.entries(
        JSON.parse(text) as Record<string, { r: number; at: number }>
      )) {
        if (now - value.at > REVISION_KEEP_MS) continue;
        const known = revisionsShown.get(key);
        if (!known || known.r < value.r) revisionsShown.set(key, value);
      }
    } catch {
      // Nothing kept, or it can't be opened: this session starts empty.
    }
  })();
  return revisionsLoad;
}

function saveRevisionsSoon() {
  const forUser = userId;
  if (!forUser || revisionsSaveTimer) return;
  revisionsSaveTimer = setTimeout(() => {
    revisionsSaveTimer = null;
    if (userId !== forUser) return;
    const now = Date.now();
    const kept = [...revisionsShown.entries()]
      .filter(([, value]) => now - value.at <= REVISION_KEEP_MS)
      .sort((a, b) => b[1].at - a[1].at)
      .slice(0, REVISION_MAX);
    void sealText(forUser, JSON.stringify(Object.fromEntries(kept)))
      .then((sealed) =>
        sealed ? saveScopeInfo(forUser, REVISIONS_SCOPE, sealed) : undefined
      )
      .catch(() => undefined);
  }, 500);
}

/** D326 (c): whether `revision` may be shown, remembering it if so. */
function admitRevision(key: string, revision: number): boolean {
  const shown = revisionsShown.get(key);
  if (revisionDecision(shown?.r, revision) === 'stale') return false;
  revisionsShown.set(key, {
    r: Math.max(shown?.r ?? 0, revision),
    at: Date.now(),
  });
  saveRevisionsSoon();
  return true;
}

function next(key: string, atLeast: number): number {
  const queued = queuedRevisions()
    .filter(
      (entry) =>
        revisionKey(entry.conversationId, userId ?? '', entry.clientId) === key
    )
    .reduce((highest, entry) => Math.max(highest, entry.revision), 0);
  const value =
    Math.max(
      atLeast,
      queued,
      revisionsShown.get(key)?.r ?? 0,
      revisionsIssued.get(key) ?? 0
    ) + 1;
  revisionsIssued.set(key, value);
  return value;
}

/**
 * The revision my next edit of a message is sealed for: one more than the
 * highest this device knows (opened, or issued for a queued edit), or than
 * `atLeast` (the server's, after STALE_REVISION).
 */
export function nextRevision(
  conversationId: string,
  clientId: string,
  atLeast = 0
): number {
  return next(revisionKey(conversationId, userId ?? '', clientId), atLeast);
}

/** The same for a group's description or picture (D326). */
export function nextGroupRevision(
  conversationId: string,
  which: GroupBinding['which'],
  atLeast = 0
): number {
  return next(groupRevisionKey(conversationId, which), atLeast);
}

// Edits waiting in the outbox already took their revisions (D326), after
// a reload too: the outbox says which (lib/outbox.ts).
let queuedRevisions: () => Array<{
  conversationId: string;
  clientId: string;
  revision: number;
}> = () => [];

export function setQueuedRevisionSource(source: typeof queuedRevisions) {
  queuedRevisions = source;
}

/** A group picture opened at this revision (pictures.ts): false if older. */
export function admitGroupRevision(
  conversationId: string,
  which: GroupBinding['which'],
  revision: number
): boolean {
  return admitRevision(groupRevisionKey(conversationId, which), revision);
}

// The v1 ceilings (D328): per chat or family, what `v1CeilingFrom` works
// from. `freshCurrent` and `v2Since` come from this session's latest fresh
// keys answer (never a cached one); `kept` (the ceiling from the last fresh
// answer), `v2Since` and `lowestV2` (the lowest epoch this device opened v2
// under) are kept in the device store; `guess` is the first envelope's
// epoch, in memory only, for before any answer at all.
interface CeilingState {
  freshCurrent: number | null;
  v2Since: number | null;
  lowestV2: number | null;
  kept: number | null;
  guess: number | null;
  loaded: Promise<void>;
}
const ceilings = new Map<string, CeilingState>();

function ceilingState(scope: string): CeilingState {
  const known = ceilings.get(scope);
  if (known) return known;
  const forUser = userId;
  const state: CeilingState = {
    freshCurrent: null,
    v2Since: null,
    lowestV2: null,
    kept: null,
    guess: null,
    loaded: Promise.resolve(),
  };
  state.loaded = (
    forUser ? loadCeilingState(forUser, scope) : Promise.resolve(null)
  )
    .then((stored) => {
      if (!stored) return;
      state.kept = stored.kept ?? state.kept;
      // A fresh answer this session wins over what was kept.
      if (state.freshCurrent === null) state.v2Since = stored.v2Since ?? null;
      if (stored.lowestV2 !== null && stored.lowestV2 !== undefined) {
        state.lowestV2 =
          state.lowestV2 === null
            ? stored.lowestV2
            : Math.min(state.lowestV2, stored.lowestV2);
      }
    })
    .catch(() => undefined);
  ceilings.set(scope, state);
  return state;
}

/**
 * This device's v1 ceiling for a chat or family (D328): `v1CeilingFrom`
 * over what it knows. Every first open of anything sealed in a scope asks.
 */
async function v1Ceiling(
  kind: ScopeKind,
  id: string,
  epoch: number
): Promise<number> {
  const state = ceilingState(`${kind}:${id}`);
  await state.loaded;
  // Before any answer: the higher of this envelope's epoch and the highest
  // this device holds a key for (never written, D328).
  if (state.guess === null) {
    const prefix = `${kind}:${id}:`;
    const held = [...contentKeys.keys()]
      .filter((cacheId) => cacheId.startsWith(prefix))
      .map((cacheId) => Number(cacheId.slice(prefix.length)))
      .filter(Number.isFinite);
    state.guess = Math.max(epoch, ...held);
  }
  return v1CeilingFrom({
    freshCurrent: state.freshCurrent,
    v2Since: state.v2Since,
    lowestV2: state.lowestV2,
    kept: state.kept,
    guess: state.guess,
  });
}

/**
 * A fresh keys answer (never a cached copy, which could be from before a
 * rotation or before v2 began): the ceiling follows its current epoch,
 * capped below where v2 began, and that is kept (D328).
 */
function noteFreshAnswer(
  scope: string,
  current: number,
  v2Since: number | null
) {
  if (current <= 0) return;
  const state = ceilingState(scope);
  state.freshCurrent = current;
  state.v2Since = v2Since;
  const kept = v2Since === null ? current : Math.min(current, v2Since - 1);
  state.kept = kept;
  const forUser = userId;
  if (!forUser) return;
  void updateCeilingState(forUser, scope, (stored) => ({
    ...stored,
    kept,
    v2Since,
  })).catch(() => undefined);
}

/** D328: a v2 envelope opened under `epoch`: no v1 belongs there or after. */
async function noteV2(kind: ScopeKind, id: string, epoch: number) {
  const scope = `${kind}:${id}`;
  const state = ceilingState(scope);
  await state.loaded;
  if (state.lowestV2 !== null && state.lowestV2 <= epoch) return;
  state.lowestV2 = epoch;
  const forUser = userId;
  if (!forUser) return;
  await updateCeilingState(forUser, scope, (stored) => ({
    ...stored,
    lowestV2:
      stored?.lowestV2 === null || stored?.lowestV2 === undefined
        ? epoch
        : Math.min(stored.lowestV2, epoch),
  })).catch(() => undefined);
}

/** A v2 group picture opened (pictures.ts). */
export function noteV2Opened(kind: ScopeKind, id: string, epoch: number) {
  return noteV2(kind, id, epoch);
}

/** For pictures.ts: may a v1 envelope open under this epoch (D328)? */
export async function v1Allowed(
  kind: ScopeKind,
  id: string,
  epoch: number
): Promise<boolean> {
  return v1Decision(epoch, await v1Ceiling(kind, id, epoch)) !== 'refuse';
}

// ---- Sender names (M76, D327) ----------------------------------------------

// For sealed items, every name shown comes from the sender id the envelope
// is bound to, through the family's members and what I call them (D143),
// never from the server's strings. Set by the app shell (useE2EE).
let senderNames: ((userId: string) => string | null) | null = null;

export function setSenderNameResolver(
  resolve: ((userId: string) => string | null) | null
) {
  senderNames = resolve;
}

// Names this device has seen for people (members it has loaded), kept
// sealed at rest in the device store (D281): someone who has left, or a
// message opened before the members load, is still named from the
// device's own record, never the server's string.
const NAMES_SCOPE = 'm76-names';
const keptNames = new Map<string, string>();
let namesLoad: Promise<void> | null = null;
let namesSaveTimer: ReturnType<typeof setTimeout> | null = null;

function loadNames(forUser: string): Promise<void> {
  namesLoad ??= (async () => {
    try {
      const sealed = await loadScopeInfo<string>(forUser, NAMES_SCOPE);
      const text = sealed ? await openText(forUser, sealed) : null;
      if (!text || userId !== forUser) return;
      for (const [id, name] of Object.entries(
        JSON.parse(text) as Record<string, string>
      )) {
        if (!keptNames.has(id)) keptNames.set(id, name);
      }
    } catch {
      // Nothing kept: names come from the members as they load.
    }
  })();
  return namesLoad;
}

/** Members' names as the app shows them, kept for later (sealed). */
export function rememberSenderNames(names: ReadonlyMap<string, string>) {
  const forUser = userId;
  if (!forUser) return;
  let changed = false;
  for (const [id, name] of names) {
    if (name && keptNames.get(id) !== name) {
      keptNames.set(id, name);
      changed = true;
    }
  }
  if (!changed || namesSaveTimer) return;
  namesSaveTimer = setTimeout(() => {
    namesSaveTimer = null;
    if (userId !== forUser) return;
    void sealText(forUser, JSON.stringify(Object.fromEntries(keptNames)))
      .then((sealed) =>
        sealed ? saveScopeInfo(forUser, NAMES_SCOPE, sealed) : undefined
      )
      .catch(() => undefined);
  }, 500);
}

/** Puts the bound sender's name on what the UI shows, or no name. */
function nameFromBinding(
  target: Record<string, unknown>,
  senderId: string | null
) {
  const sender = target.sender as
    | { displayName?: unknown; deletedAt?: unknown }
    | null
    | undefined;
  // A deleted account (M51) is named by the app's own words, never the
  // server's string.
  const deleted = Boolean(target.senderDeleted || sender?.deletedAt);
  const name = !senderId
    ? ''
    : deleted
      ? t('account.deletedAccount')
      : (senderNames?.(senderId) ?? keptNames.get(senderId) ?? '');
  if ('senderName' in target) target.senderName = name;
  if (sender && typeof sender === 'object') {
    target.sender = { ...sender, displayName: name };
  }
}

// Counted once per item per session, with no identifiers (D327, D254).
const counted = new Set<string>();
function countOnce(
  name:
    | 'e2ee_envelope_unverified'
    | 'e2ee_envelope_stale_edit'
    | 'e2ee_envelope_downgrade',
  item: string
) {
  const key = `${name}:${item}`;
  if (counted.has(key)) return;
  counted.add(key);
  countClientFailure(name);
}

// ---- Opening what arrives ---------------------------------------------------

const LABELS: Record<string, string> = {
  image: 'Photo',
  gif: 'GIF',
  video: 'Video',
  voice: 'Voice note',
  file: 'File',
};

type Sealed = Record<string, unknown> & {
  encryption: string;
  content: string;
  conversationId: string;
  clientId: string;
  keyEpoch: number;
  e2ee?: OpenedE2EE;
};

/** The message envelope versions this app can open (D59, D324). */
const KNOWN_ENCRYPTION = new Set(['v1', 'v2']);

/**
 * A sealed message, quote, chat-list line or group description. Any
 * `encryption` counts, not only the ones this app knows: one from a newer
 * app is locked here, never shown as its ciphertext.
 */
function isSealed(value: Record<string, unknown>): value is Sealed {
  return (
    typeof value.encryption === 'string' &&
    value.encryption !== '' &&
    typeof value.content === 'string' &&
    value.e2ee === undefined &&
    (!KNOWN_ENCRYPTION.has(value.encryption) ||
      (typeof value.conversationId === 'string' &&
        typeof value.clientId === 'string' &&
        typeof value.keyEpoch === 'number'))
  );
}

/** Puts an opened envelope on its message (exported for tests). */
export function apply(target: Sealed, envelope: Envelope) {
  const meta = envelope.meta ?? {};
  const files = (
    (target.metadata as { files?: string[] } | null | undefined)?.files ?? []
  ).filter(Boolean);
  const type = typeof target.type === 'string' ? target.type : 'text';
  const plain = (target.metadata ?? {}) as { album?: unknown };
  // A gathering's words are checked here (M71, D282): anything that isn't
  // one leaves the card with the "update Twinden" line (D222), never raw.
  if (envelope.gathering !== undefined) {
    const checked = gatheringContentSchema.safeParse(envelope.gathering);
    const { gathering: _gathering, ...rest } = envelope;
    envelope = checked.success ? { ...rest, gathering: checked.data } : rest;
    // The ring names it even when it was opened from history (D286).
    if (checked.success && typeof target.id === 'string') {
      const state = target.gathering as { startsAt?: unknown } | undefined;
      rememberGatheringTitle(
        target.id,
        checked.data.title,
        typeof state?.startsAt === 'string' ? state.startsAt : undefined
      );
    }
  }
  target.e2ee = { envelope };
  // An album's content is its caption, a poll's its question (M60), a
  // gathering's its title (M71).
  target.content =
    type === 'text' || type === 'album'
      ? (envelope.text ?? '')
      : type === 'poll'
        ? (envelope.poll?.question ?? '')
        : type === 'gathering'
          ? (envelope.gathering?.title ?? '')
          : FILE_TYPES.has(type)
            ? (files[0] ?? '')
            : '';
  const { file: _file, thumb: _thumb, plainFile: _plain, ...visible } = meta;
  target.metadata = {
    ...visible,
    ...(files[1] && FILE_TYPES.has(type) ? { thumbnailUrl: files[1] } : {}),
    files,
    // An album's counts are plain metadata (D219); keep them.
    ...(plain.album ? { album: plain.album } : {}),
  };
  if ('preview' in target) {
    const text = (envelope.text ?? '').replace(/\s+/g, ' ').trim();
    target.preview =
      type === 'text'
        ? text
        : type === 'album'
          ? text || t('chat.type.album')
          : type === 'poll'
            ? (envelope.poll?.question ?? '')
            : type === 'gathering'
              ? (envelope.gathering?.title ?? '')
              : (LABELS[type] ?? 'Attachment');
    // The chat list says how many (D219): "Sent 4 photos".
    if (type === 'album' && envelope.album) {
      target.album = {
        photos: envelope.album.filter((item) => item.kind !== 'video').length,
        videos: envelope.album.filter((item) => item.kind === 'video').length,
      };
    }
  }
}

/**
 * When something sealed this device opened was sealed, at the latest (D347):
 * a whole message's time, or its edit's (an edit of a message from before
 * encryption is sealed later than the message). Quotes, tiles and anything
 * else that doesn't say whether it was edited prove nothing.
 */
function sealedBy(target: Record<string, unknown>): number {
  if (!('editedAt' in target) || typeof target.createdAt !== 'string') {
    return NaN;
  }
  const created = Date.parse(target.createdAt);
  const edited =
    typeof target.editedAt === 'string' ? Date.parse(target.editedAt) : NaN;
  return Number.isFinite(edited) ? Math.max(created, edited) : created;
}

/** Scope → the earliest time something sealed was opened there (D347). */
type SealedTimes = Map<string, number>;

function noteSealed(times: SealedTimes, scope: string, at: number) {
  if (!Number.isFinite(at)) return;
  const known = times.get(scope);
  if (known === undefined || at < known) times.set(scope, at);
}

/** Who sent a sealed message, as its payload says (`sender.id` or `senderId`). */
function senderOf(target: Sealed): string | null {
  const sender = target.sender as { id?: unknown } | null | undefined;
  if (typeof sender?.id === 'string') return sender.id;
  return typeof target.senderId === 'string' ? target.senderId : null;
}

/**
 * Not shown, and never as from anyone (D327): a key is held but what came
 * doesn't open as what it claims to be.
 */
function unverified(target: Sealed) {
  target.e2ee = { locked: true, unverified: true };
  target.content = '';
  if ('preview' in target) target.preview = t('chat.message.unverified');
  // Never shown as from anyone (D327).
  nameFromBinding(target, null);
}

/** The v2 associated data a message (or group description) claims, or null. */
function claimedAad(target: Sealed) {
  try {
    if (target.kind === 'description') {
      return groupAadV2({
        conversationId: target.conversationId,
        clientId: target.clientId,
        which: 'description',
        epoch: target.keyEpoch,
        revision: typeof target.revision === 'number' ? target.revision : 0,
      });
    }
    const senderId = senderOf(target);
    if (!senderId) return null;
    return messageAadV2({
      conversationId: target.conversationId,
      senderId,
      clientId: target.clientId,
      // Scheduled messages are text and don't say so.
      type: typeof target.type === 'string' ? target.type : 'text',
      epoch: target.keyEpoch,
      revision: typeof target.revision === 'number' ? target.revision : 0,
    });
  } catch {
    // A field that can't be bound (D324): fails closed.
    return null;
  }
}

async function openOne(target: Sealed, times: SealedTimes) {
  const lockedHere = () => {
    target.e2ee = { locked: true };
    target.content = '';
    if ('preview' in target) target.preview = t('chat.home.encrypted');
  };
  // A version from a newer Twinden: locked, never shown as text.
  if (!KNOWN_ENCRYPTION.has(target.encryption)) {
    lockedHere();
    return;
  }
  const key = await keyFor('chat', target.conversationId, target.keyEpoch);
  if (!key) {
    // No key here yet (D57): locked until one arrives, not a mismatch.
    lockedHere();
    return;
  }
  const forUser = userId;
  if (forUser) await Promise.all([loadRevisions(forUser), loadNames(forUser)]);
  // Every first look at a chat sets its ceiling, v1 or v2 (D328).
  const ceiling = await v1Ceiling(
    'chat',
    target.conversationId,
    target.keyEpoch
  );
  const item = `${target.conversationId}:${target.clientId}`;
  const description = target.kind === 'description';
  if (target.encryption === 'v2') {
    const aad = claimedAad(target);
    let envelope: Envelope | null = null;
    if (aad) {
      try {
        envelope = await openEnvelope<Envelope>(key, target.content, aad);
      } catch {
        // Not what it claims (another sender, type, epoch, chat or revision).
      }
    }
    if (!envelope) {
      countOnce('e2ee_envelope_unverified', item);
      unverified(target);
      return;
    }
    const revision = typeof target.revision === 'number' ? target.revision : 0;
    const shownKey = description
      ? groupRevisionKey(target.conversationId, 'description')
      : revisionKey(target.conversationId, senderOf(target)!, target.clientId);
    if (!admitRevision(shownKey, revision)) {
      countOnce('e2ee_envelope_stale_edit', `${item}:${revision}`);
      unverified(target);
      return;
    }
    // Edited if and only if the bound revision says so (D326 b).
    if (!description && 'editedAt' in target) {
      if (revision === 0) target.editedAt = null;
      else if (!target.editedAt) target.editedAt = target.createdAt ?? true;
    }
    await noteV2('chat', target.conversationId, target.keyEpoch);
    apply(target, envelope);
    // What apply checked (a gathering's words, D282), not the raw envelope.
    target.e2ee = { envelope: target.e2ee!.envelope, bound: true };
    if (!description) nameFromBinding(target, senderOf(target));
    noteSealed(times, `chat:${target.conversationId}`, sealedBy(target));
    return;
  }
  // v1 (D328): only up to this device's ceiling for the chat, and never
  // vouched for.
  if (v1Decision(target.keyEpoch, ceiling) === 'refuse') {
    countOnce('e2ee_envelope_downgrade', item);
    unverified(target);
    return;
  }
  try {
    const envelope = await openEnvelope<Envelope>(
      key,
      target.content,
      messageAad(target.conversationId, target.clientId)
    );
    apply(target, envelope);
    target.e2ee = { envelope: target.e2ee!.envelope, bound: false };
    // Still not the server's string: the name of whom it claims (marked).
    if (!description) nameFromBinding(target, senderOf(target));
    noteSealed(times, `chat:${target.conversationId}`, sealedBy(target));
  } catch {
    // Tampered with, or sealed for another chat: never shown (D59).
    countOnce('e2ee_envelope_unverified', item);
    unverified(target);
  }
}

// ---- Family app records (D65) ---------------------------------------------

/** What a family-app record's envelope can hold. */
// `picture` is a family's picture, `pkey` a person's picture key wrapped
// for the family (M72, D291, D292).
export type AppKind =
  | 'link'
  | 'doc'
  | 'name'
  | 'item'
  | 'picture'
  | 'pkey'
  // M76 (D329): a template's name and items, apart from a list's.
  | 'tname'
  | 'titem';

const SEALED =
  /^e2ee:(v\d+):([0-9a-f-]{36}):(\d+):([a-z]+):([A-Za-z0-9+/]+={0,2})$/;
// Any version's envelope, for its kind: `e2ee:<version>:<family>:<epoch>:<kind>:…`.
const SEALED_ANY = /^e2ee:[^:]*:[^:]*:[^:]*:([a-z]+):/;
const appAad = (familyId: string, kind: AppKind) =>
  `twinden app v1|${familyId}|${kind}`;
// Pictures and picture keys keep v1: they are bound to their owner inside
// the envelope (D292), and a record id isn't what they're found by (D325).
const PICTURE_KINDS = new Set<AppKind>(['picture', 'pkey']);

/**
 * Seals a family-app record as one self-describing string the server can
 * store but not read: `e2ee:v2:<familyId>:<epoch>:<kind>:<ciphertext>`,
 * bound to the record's id (M76, D329). Pictures and picture keys
 * (`recordId` absent) stay `e2ee:v1:`.
 */
export async function sealApp(
  familyId: string,
  kind: AppKind,
  value: object,
  recordId?: string,
  /** A list item's list, a template item's template; else the family. */
  parentId: string = familyId
): Promise<string> {
  await ready;
  const { epoch, key } = await sendingKey('family', familyId);
  if (PICTURE_KINDS.has(kind)) {
    const sealed = await sealEnvelope(key, value, appAad(familyId, kind));
    return `e2ee:v1:${familyId}:${epoch}:${kind}:${sealed}`;
  }
  if (!recordId) throw new Error('E2EE_RECORD_ID');
  const sealed = await sealEnvelope(
    key,
    value,
    appAadV2({ familyId, kind, parentId, recordId, epoch })
  );
  return `e2ee:v2:${familyId}:${epoch}:${kind}:${sealed}`;
}

/**
 * Writes a sealed record, sealing again once if the family's key moved on.
 * `recordId` is the record's id: its own for an update, a fresh one the
 * write sends for a new record (D329).
 */
export async function writeSealedApp<T>(
  familyId: string,
  kind: AppKind,
  value: object,
  write: (sealed: string) => Promise<T>,
  recordId?: string,
  parentId?: string
): Promise<T> {
  try {
    return await write(
      await sealApp(familyId, kind, value, recordId, parentId)
    );
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.code === 'STALE_KEY' || err.code === 'ROTATION_REQUIRED')
    ) {
      forgetScope('family', familyId);
      return write(await sealApp(familyId, kind, value, recordId, parentId));
    }
    throw err;
  }
}

/**
 * Opens one sealed picture or picture key, only if it is of this family and
 * kind (D292: a record moved from another family or kind never opens).
 * null when it can't.
 */
export async function openAppRecord<T = Record<string, unknown>>(
  sealed: string,
  familyId: string,
  kind: AppKind
): Promise<T | null> {
  const match = SEALED.exec(sealed);
  if (!match || match[1] !== 'v1' || match[2] !== familyId || match[4] !== kind)
    return null;
  const key = await keyFor('family', familyId, Number(match[3]));
  if (!key) return null;
  try {
    return await openEnvelope<T>(key, match[5]!, appAad(familyId, kind));
  } catch {
    return null;
  }
}

/** Added to an opened family-app record. */
export interface OpenedApp {
  /** What was inside, including file secrets for documents. */
  envelope?: Record<string, unknown>;
  locked?: boolean;
  /**
   * Never shown: the key is here but it isn't this record's (M76, D327),
   * or plain where only sealed records can be (D347).
   */
  unverified?: boolean;
  /** Bound to this record's id (v2); false for an older v1 one (D328). */
  bound?: boolean;
}

type SealedApp = Record<string, unknown> & {
  sealed: string;
  e2eeApp?: OpenedApp;
};

// Any `e2ee:` record is sealed, whatever its version: one from a newer app
// is locked here, never taken for a plain one and written over. Only a
// record without the prefix is plain (from before M28).
function isSealedApp(value: Record<string, unknown>): value is SealedApp {
  return (
    typeof value.sealed === 'string' &&
    value.sealed.startsWith('e2ee:') &&
    value.e2eeApp === undefined
  );
}

/**
 * A family-app record this device can't open: its key hasn't come, it was
 * sealed by a newer app, or it isn't what it claims (D327). It can't be
 * edited, renamed or used as a template here: that would write over it.
 */
export function isLockedApp(record: { e2eeApp?: OpenedApp }): boolean {
  return record.e2eeApp?.locked === true;
}

// Envelope fields that are secrets, kept off the record itself; and the
// record's id, which is the row's own (D329).
const APP_SECRETS = new Set(['file', 'thumb', 'plainFile', 'id']);

/** Where a record was found: the family being viewed, its enclosing record. */
interface AppContext {
  familyId?: string;
  enclosingId?: string;
}

async function openApp(
  target: SealedApp,
  context: AppContext,
  times: SealedTimes
) {
  const match = SEALED.exec(target.sealed);
  if (!match) {
    // A picture of any version is lib/e2ee/pictures.ts's to open (D292).
    const anyKind = SEALED_ANY.exec(target.sealed)?.[1];
    if (PICTURE_KINDS.has(anyKind as AppKind)) return;
    lockApp(target, anyKind);
    return;
  }
  const [, version, familyId, epochText, kind, sealed] = match as unknown as [
    string,
    string,
    string,
    string,
    AppKind,
    string,
  ];
  // Pictures and picture keys are opened by lib/e2ee/pictures.ts alone:
  // copied onto a row here, a key record's userId would replace the
  // row's own, which the opener checks it against (D292).
  if (PICTURE_KINDS.has(kind)) return;
  // A version from a newer Twinden: locked, never what the server wrote.
  if (version !== 'v1' && version !== 'v2') {
    lockApp(target, kind);
    return;
  }
  const item = `${familyId}:${String(target.id)}:${kind}`;
  // Only in the family being viewed (D329).
  if (context.familyId && context.familyId !== familyId) {
    countOnce('e2ee_envelope_unverified', item);
    lockApp(target, kind, true);
    return;
  }
  const epoch = Number(epochText);
  const key = await keyFor('family', familyId, epoch);
  if (!key) {
    lockApp(target, kind);
    return;
  }
  // Every first look at a family sets its ceiling, v1 or v2 (D328).
  const ceiling = await v1Ceiling('family', familyId, epoch);
  let envelope: Record<string, unknown> | null = null;
  if (version === 'v2') {
    // A list item belongs to its list, a template item to its template
    // (D329); everything else to the family.
    const parentId =
      kind === 'item'
        ? typeof target.listId === 'string'
          ? target.listId
          : context.enclosingId
        : kind === 'titem'
          ? context.enclosingId
          : familyId;
    try {
      if (typeof target.id !== 'string' || !parentId)
        throw new Error('E2EE_RECORD_ID');
      envelope = await openEnvelope<Record<string, unknown>>(
        key,
        sealed,
        appAadV2({ familyId, kind, parentId, recordId: target.id, epoch })
      );
    } catch {
      // Another record's, list's, kind's or family's (D329): never shown.
    }
  } else {
    if (v1Decision(epoch, ceiling) === 'refuse') {
      countOnce('e2ee_envelope_downgrade', item);
      lockApp(target, kind, true);
      return;
    }
    try {
      envelope = await openEnvelope<Record<string, unknown>>(
        key,
        sealed,
        appAad(familyId, kind)
      );
    } catch {
      // Tampered with, or sealed for another family: never shown.
    }
  }
  if (!envelope) {
    countOnce('e2ee_envelope_unverified', item);
    lockApp(target, kind, true);
    return;
  }
  if (version === 'v2') await noteV2('family', familyId, epoch);
  for (const [field, value] of Object.entries(envelope)) {
    if (!APP_SECRETS.has(field)) target[field] = value;
  }
  target.e2eeApp = { envelope, bound: version === 'v2' };
  // A list's or item's last change was this sealed state (D347). Links,
  // documents and templates don't say when they last changed, and one from
  // before encryption may have been sealed since.
  if (typeof target.updatedAt === 'string') {
    noteSealed(times, `family:${familyId}`, Date.parse(target.updatedAt));
  }
}

// What a locked record shows in its plain columns: the server's own
// placeholders (D65), whatever the server sent there (D347).
const LOCKED_WORDS: Record<string, Record<string, string>> = {
  link: { url: 'e2ee' },
  doc: { fileName: 'Encrypted file' },
  name: { name: 'Encrypted list' },
  tname: { name: 'Encrypted list' },
  item: { text: 'Encrypted item' },
  titem: { text: 'Encrypted item' },
};

/**
 * A sealed record this device can't open: nothing the server wrote shows.
 * `unverified`: the key is here but it isn't what it claims (D327).
 */
function lockApp(
  target: Record<string, unknown>,
  kind: string | undefined,
  unverified = false
) {
  target.e2eeApp = unverified
    ? { locked: true, unverified: true }
    : { locked: true };
  for (const [field, value] of Object.entries(LOCKED_WORDS[kind ?? ''] ?? {})) {
    if (field in target) target[field] = value;
  }
  for (const field of [
    'note',
    'title',
    'description',
    'siteName',
    'imageUrl',
    'folder',
  ]) {
    if (field in target) target[field] = null;
  }
  if ('image' in target) delete target.image;
  if (Array.isArray(target.tags)) target.tags = [];
}

// ---- Plain text only from before encryption (D347) -------------------------

// What a person sends. A system line is the server's own words, never sealed.
const SENT_TYPES = new Set([
  'text',
  'album',
  'poll',
  'gathering',
  ...FILE_TYPES,
]);

/**
 * A message, quote or chat-list line with no `encryption` and something to
 * show: plain text, which is genuine only from before its chat's encryption.
 */
function isPlainMessage(value: Record<string, unknown>): boolean {
  if (typeof value.encryption === 'string' && value.encryption !== '') {
    return false;
  }
  // A scheduled message (D239) has no type: it's text.
  const scheduled = typeof value.scheduledFor === 'string';
  if (
    !scheduled &&
    (typeof value.type !== 'string' || !SENT_TYPES.has(value.type))
  ) {
    return false;
  }
  if (typeof value.createdAt !== 'string') return false;
  if (!('content' in value) && !('preview' in value)) return false;
  if (value.deletedAt || value.deleted === true) return false;
  const files = (value.metadata as { files?: unknown } | null | undefined)
    ?.files;
  return (
    (typeof value.content === 'string' && value.content !== '') ||
    (typeof value.preview === 'string' && value.preview !== '') ||
    (Array.isArray(files) && files.length > 0)
  );
}

/** A family-app record with no `e2ee:` envelope: plain, from before D65. */
function isPlainApp(value: Record<string, unknown>): boolean {
  return (
    'sealed' in value &&
    !(typeof value.sealed === 'string' && value.sealed.startsWith('e2ee:')) &&
    typeof value.createdAt === 'string'
  );
}

/** Never shown, not waiting for a key either (D347). */
function refusePlain(target: Record<string, unknown>) {
  target.e2ee = { locked: true, unverified: true };
  target.content = '';
  if ('metadata' in target) target.metadata = null;
  if ('preview' in target) target.preview = t('chat.message.unverified');
}

/** A plain record dated after its family's encryption: nothing of it shown. */
function refusePlainApp(target: Record<string, unknown>) {
  const line = t('chat.message.unverified');
  target.e2eeApp = { locked: true, unverified: true };
  for (const field of ['name', 'text', 'title', 'fileName']) {
    if (typeof target[field] === 'string') target[field] = line;
  }
  for (const field of ['note', 'description', 'siteName', 'imageUrl']) {
    if (field in target) target[field] = null;
  }
  if ('folder' in target) target.folder = null;
  if ('image' in target) delete target.image;
  if (Array.isArray(target.tags)) target.tags = [];
  // A template's own items (a list's items are records, checked one by one).
  if (
    Array.isArray(target.items) &&
    target.items.every(
      (item) => item && typeof item === 'object' && !('id' in item)
    )
  ) {
    target.items = [];
  }
  if (typeof target.url === 'string') {
    // A document's file, or a link's address, as the server's placeholders.
    target.url = 'fileName' in target ? '' : 'e2ee';
  }
  if ('mimeType' in target) target.mimeType = 'application/octet-stream';
}

// Scopes whose keys answer was asked for this visit to learn when their
// encryption began (D347), and items already counted.
const plainLooked = new Set<string>();
const downgradeCounted = new Set<string>();

/**
 * Decides every plain message and record found (D347): shown when dated
 * before its scope's encryption, as this device knows it; refused (and
 * counted, with no identifiers) from then on. A scope this device knows no
 * encryption for asks for its keys answer once a visit, which says when.
 */
async function decidePlain(
  plain: Array<{ target: Record<string, unknown>; scope: string; app: boolean }>
) {
  await ready;
  const unknown = new Set<string>();
  for (const { scope, target } of plain) {
    if (
      plainDecision(trust, scope, Date.parse(String(target.createdAt))) ===
        'unknown' &&
      !plainLooked.has(scope)
    ) {
      unknown.add(scope);
    }
  }
  await Promise.all(
    [...unknown].map((scope) => {
      plainLooked.add(scope);
      const [kind, ...rest] = scope.split(':');
      return fetchScope(kind as ScopeKind, rest.join(':')).catch(() => null);
    })
  );
  for (const { target, scope, app } of plain) {
    const createdAt = String(target.createdAt);
    if (plainDecision(trust, scope, Date.parse(createdAt)) !== 'refuse') {
      continue;
    }
    if (app) refusePlainApp(target);
    else refusePlain(target);
    const item = `${scope}:${String(target.id ?? '')}:${createdAt}`;
    if (!downgradeCounted.has(item)) {
      downgradeCounted.add(item);
      countClientFailure('e2ee_plaintext_downgrade');
    }
  }
}

const FAMILY_IN_URL = /\/families\/([0-9a-f-]{36})(?:[/?]|$)/i;

/**
 * Opens every encrypted message, reply quote, chat-list preview and
 * family-app record inside an API response or socket payload, in place, and
 * refuses plain text dated after its chat's or family's encryption (D347).
 * Only for what just came from the server: what a device opened is marked
 * here alone, so any `e2ee` or `e2eeApp` the server sends is dropped first.
 * `context.url` is the request's: a family's records open only in the
 * family it names (D329).
 */
export async function openAll(
  data: unknown,
  context: { url?: string } = {}
): Promise<unknown> {
  const pending: Promise<void>[] = [];
  const times: SealedTimes = new Map();
  const plain: Array<{
    target: Record<string, unknown>;
    scope: string;
    app: boolean;
  }> = [];
  const fromUrl = context.url
    ? FAMILY_IN_URL.exec(context.url)?.[1]?.toLowerCase()
    : undefined;
  const visit = (
    value: unknown,
    chat: string | undefined,
    family: string | undefined,
    app: AppContext
  ) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => visit(entry, chat, family, app));
      return;
    }
    if (!value || typeof value !== 'object') return;
    const record = value as Record<string, unknown>;
    // Opened, locked and unverified are this device's to say (D347).
    delete record.e2ee;
    delete record.e2eeApp;
    // Its chat: its own, a chat's (for its tile), else the one it's in.
    const ownChat =
      typeof record.conversationId === 'string'
        ? record.conversationId
        : 'lastMessage' in record && typeof record.id === 'string'
          ? record.id
          : chat;
    const ownFamily =
      typeof record.familyId === 'string' ? record.familyId : family;
    if (isSealed(record)) {
      pending.push(openOne(record, times));
    } else if (
      typeof record.encryption === 'string' &&
      record.encryption !== '' &&
      typeof record.content === 'string'
    ) {
      // Says it's sealed but lacks what opens it: locked, never its text.
      record.e2ee = { locked: true };
      record.content = '';
      if ('preview' in record) record.preview = t('chat.home.encrypted');
    } else if (ownChat && isPlainMessage(record)) {
      plain.push({ target: record, scope: `chat:${ownChat}`, app: false });
    }
    if (isSealedApp(record)) {
      pending.push(openApp(record, app, times));
    } else if (ownFamily && isPlainApp(record)) {
      plain.push({ target: record, scope: `family:${ownFamily}`, app: true });
    }
    // The family being viewed: the request's, else the one named around it.
    const inner: AppContext = {
      familyId: fromUrl ?? ownFamily ?? app.familyId,
      enclosingId: typeof record.id === 'string' ? record.id : app.enclosingId,
    };
    for (const child of Object.values(record)) {
      if (child && typeof child === 'object') {
        visit(child, ownChat, ownFamily, inner);
      }
    }
  };
  visit(data, undefined, undefined, { familyId: fromUrl });
  if (pending.length > 0) await Promise.all(pending);
  // What opened here dates each scope's encryption at the latest (D347).
  const owner = userId;
  if (owner && times.size > 0) {
    await changeTrust(owner, (record) => {
      let next = record;
      for (const [scope, at] of times) {
        next = notePlainBefore(next, scope, at, 'opened');
      }
      return next;
    });
  }
  if (plain.length > 0) await decidePlain(plain);
  return data;
}

/** A key arrived from another device: try what was locked again soon. */
export function keysArrived(kind: ScopeKind, id: string) {
  for (const cacheId of [...missingSince.keys()]) {
    if (cacheId.startsWith(`${kind}:${id}:`)) missingSince.delete(cacheId);
  }
  // A holder answered: no need to ask again until the next connect.
  cancelAskRetries();
  lackedKeys = false;
  // The fresh key (D262) has what it needed: the banner can go. (The
  // replaced key's row goes once a copy for the fresh key actually opens —
  // `keys:shared` goes to every device of this user.)
  if (storageState === 'replaced') setStorageState('ok');
  scopeKeyListeners.forEach((listener) => listener(kind, id));
}

const scopeKeyListeners = new Set<(kind: ScopeKind, id: string) => void>();

/** Runs `listener` after `keysArrived` (sealed pictures retry, D294). */
export function onScopeKeysArrived(
  listener: (kind: ScopeKind, id: string) => void
): () => void {
  scopeKeyListeners.add(listener);
  return () => {
    scopeKeyListeners.delete(listener);
  };
}

// ---- Sharing keys with other devices (D57) ---------------------------------

/** What one round of sharing handed over (D138 shows it). */
export interface SharedSummary {
  /** Chats whose keys went to at least one device. */
  chats: number;
  /** Devices that received at least one key. */
  devices: number;
}

/** What a run handed over, by id, so two runs can be added up. */
type Handed = { chats: Set<string>; devices: Set<string> };
const nothingHanded = (): Handed => ({ chats: new Set(), devices: new Set() });

let sharing: Promise<Handed> | null = null;
// The run that follows the current one. A request made while a run is under
// way can't be served by it (its pending list predates the request; a device
// may have registered since), so callers wait for the follow-up run too and
// get both runs' results together — the D138/D161 handover screens then say
// what was actually handed over.
let nextSharing: Promise<Handed> | null = null;

// ---- Asking for keys this device lacks (D57) --------------------------------

/**
 * Asks family devices again for keys this device still lacks (D57). The
 * registration's `keys:needed` goes out once; a device whose keys never
 * came — the phone was reconnecting after the scanner, say — would
 * otherwise stay locked for good, since a reload reuses the stored device
 * key. Called on every connect; the server only announces when a fellow
 * device holds something this one lacks.
 */
export async function askForKeys(): Promise<boolean> {
  await ready;
  const forUser = userId;
  if (!device || !forUser) return false;
  const mine = device;
  try {
    const asked = (await api.askForKeys(mine.id)).data.asked;
    if (asked) {
      lackedKeys = true;
    } else if (lackedKeys) {
      // An earlier ask found keys missing and now none are: they came while
      // no `keys:shared` reached this page (the socket may be down, D262).
      // Locked views reload as if it had.
      lackedKeys = false;
      missingSince.clear();
      cancelAskRetries();
      if (storageState === 'replaced') setStorageState('ok');
      arrivedListeners.forEach((listener) => listener());
    }
    return asked;
  } catch (err) {
    // Only the route's own "no such device of yours" counts, and only once
    // the server confirms the key is gone: a plain 404 (a server without
    // this route during a rollback) must never cost this device its key.
    if (
      err instanceof ApiError &&
      err.status === 404 &&
      err.code === 'UNKNOWN_DEVICE'
    ) {
      return replaceLostKey(forUser, mine.id);
    }
    return false;
  }
}

/**
 * The server no longer knows this device's key (it went with a passkey, D55,
 * while this device was away and setUp couldn't check): nobody could share
 * to it. A fresh key, registered, asks for everything (D57). Nothing is
 * replaced unless the device list confirms the old id is gone.
 */
async function replaceLostKey(forUser: string, lostId: string) {
  try {
    const mine = await api.getUserDeviceKeys(forUser);
    if (mine.data.devices.some((entry) => entry.id === lostId)) return false;
    // Under the key lock, and only while `self` is still the lost key:
    // another tab may have replaced it meanwhile, and its good key must not
    // be set aside as lost and overwritten (D262).
    const outcome = await withKeyLock<{ key: StoredDevice; fresh: boolean }>(
      async () => {
        const current = await loadDevice(forUser);
        if (current && current.id !== lostId) {
          return { key: current, fresh: false };
        }
        // `self` is kept aside before the fresh key overwrites it.
        await setAsideSelf(forUser, true);
        return { key: await register(forUser), fresh: true };
      }
    );
    const next = outcome.key;
    if (userId === forUser && device?.id === lostId) {
      device = next;
      contentKeys.clear();
      scopeInfo.clear();
      missingSince.clear();
      loading.clear();
    }
    return outcome.fresh;
  } catch {
    return false;
  }
}

// After an ask that found keys missing, ask again at these moments (from the
// ask) unless keys arrive first: the holder may have swallowed the first
// announcement (a native sharer mid-run), and a laptop on a steady connection
// would otherwise wait for a reload. Later than the server's 30 s throttle.
const ASK_AGAIN_AT_MS = [40_000, 120_000];
let askTimers: ReturnType<typeof setTimeout>[] = [];
// Which ask the timers belong to: a cancel (keys arrived, a new connect, a
// sign-out) moves it on, and anything from an earlier ask — a timer, or an
// ask still waiting on `ready` — no longer counts.
let askGeneration = 0;
// This page load registered the key: that announced already (D55).
let justRegistered = false;
// An ask on this page found keys missing; the next "nothing missing" means
// they arrived.
let lackedKeys = false;
const arrivedListeners = new Set<() => void>();

/**
 * Runs `listener` when an ask learns the missing keys arrived without a
 * `keys:shared` event reaching this page (D262). Returns unsubscribe.
 */
export function onKeysArrivedQuietly(listener: () => void): () => void {
  arrivedListeners.add(listener);
  return () => {
    arrivedListeners.delete(listener);
  };
}

// Asks from the page itself (load, coming back to the tab) rather than the
// socket: at most one per 30 s, the server's own announcement window.
const WAKE_ASK_GAP_MS = 30_000;
let lastWakeAsk = 0;

/**
 * The ask on app load and on `visibilitychange`, independent of the socket
 * (D262): a page whose WebSocket never connects — an extension, a proxy —
 * would otherwise never ask, and its stored key would stay without keys
 * for good. Throttled; the connect's ask is not.
 */
export function askForKeysOnWake(): Promise<boolean> {
  const now = Date.now();
  if (now - lastWakeAsk < WAKE_ASK_GAP_MS) return Promise.resolve(false);
  lastWakeAsk = now;
  return askForKeysUntilShared();
}

function cancelAskRetries() {
  askGeneration += 1;
  askTimers.forEach((timer) => clearTimeout(timer));
  askTimers = [];
}

/**
 * On connect: asks for missing keys now (unless registering just did), and
 * again later while nothing has arrived. Stops at the first `keys:shared`
 * (`keysArrived`) or the next connect.
 */
export async function askForKeysUntilShared(): Promise<boolean> {
  cancelAskRetries();
  lastWakeAsk = Date.now();
  const generation = askGeneration;
  await ready;
  if (generation !== askGeneration) return false;
  const registered = justRegistered;
  justRegistered = false;
  const asked = registered ? device !== null : await askForKeys();
  if (!asked || generation !== askGeneration) return false;
  for (const at of ASK_AGAIN_AT_MS) {
    askTimers.push(
      setTimeout(() => {
        if (generation === askGeneration) void askForKeys();
      }, at)
    );
  }
  return true;
}

/** Gives every key this device holds to members' devices that lack it. */
export async function shareKeys(): Promise<SharedSummary> {
  const handed = await runShare();
  return { chats: handed.chats.size, devices: handed.devices.size };
}

type PendingShare = Awaited<
  ReturnType<typeof api.getPendingKeyShares>
>['data'][number];

/**
 * The pending devices a key may be wrapped for (D302): each must be listed
 * as some member's device in the scope's own keys answer, and so recorded
 * (D300) — the pending list doesn't say whose device it is. A device the
 * last answer didn't list asks for a fresh one first, which records it and,
 * for a person already known, announces it (D303).
 */
/**
 * Devices a keys answer lists that aren't a recorded current device of a
 * current member (or mine): someone just added, or someone deferred (D301).
 */
function outsiders(
  kind: ScopeKind,
  id: string,
  devices: ReadonlyArray<{ userId: string; publicKey: string }>
) {
  const record = trust;
  const me = userId;
  if (!record) return [];
  const inScope = currentMembers(record, `${kind}:${id}`);
  return devices.filter(
    (d) =>
      !(
        (d.userId === me || inScope.has(d.userId)) &&
        isRecorded(record, d.userId, d.publicKey)
      )
  );
}

/**
 * A keys answer that lists devices of people not known as members: the
 * members are read now, throttle or not, and if any of them turned out to
 * be members, a fresh answer records their devices (announcing a known
 * person's new one, D303). Otherwise the answer as it was.
 */
async function withNewMembers(
  kind: ScopeKind,
  id: string,
  info: ScopeKeys
): Promise<ScopeKeys> {
  const forUser = userId;
  if (!forUser || !trust) return info;
  const scope = `${kind}:${id}`;
  const strangersNow = [
    ...new Set(
      info.devices
        .map((d) => d.userId)
        .filter(
          (user) => user !== forUser && !currentMembers(trust!, scope).has(user)
        )
    ),
  ];
  if (strangersNow.length === 0) return info;
  const { members } = await scopeMembers(forUser, kind, id, strangersNow, true);
  if (!strangersNow.some((user) => members.has(user))) return info;
  return (await fetchScope(kind, id)) ?? info;
}

async function recordedTargets(
  scope: ScopeKind,
  scopeId: string,
  entries: PendingShare[]
): Promise<PendingShare[]> {
  const listed = (info: ScopeKeys | null | undefined, entry: PendingShare) =>
    info?.devices.find(
      (d) => d.id === entry.deviceKeyId && d.publicKey === entry.publicKey
    );
  let info = scopeInfo.get(`${scope}:${scopeId}`);
  // A pending device the kept answer doesn't list, or lists under someone
  // not known as a member yet (just added to the group): a fresh answer,
  // which re-reads the members for anyone new (D301), so they get their
  // keys now rather than after the members are next read.
  const strangers = (current: ScopeKeys | null | undefined) =>
    entries.some((entry) => {
      const owner = listed(current, entry);
      return !owner || outsiders(scope, scopeId, [owner]).length > 0;
    });
  if (strangers(info)) {
    info = (await fetchScope(scope, scopeId)) ?? undefined;
  }
  // Listed under someone not known as a member yet (just added to the
  // group): read the members now, so they get their keys now (D301).
  if (info) info = await withNewMembers(scope, scopeId, info);
  const record = trust;
  const me = userId;
  if (!info || !record) return [];
  const inScope = currentMembers(record, `${scope}:${scopeId}`);
  return entries.flatMap((entry) => {
    const owner = listed(info, entry);
    return owner !== undefined &&
      (owner.userId === me || inScope.has(owner.userId)) &&
      isRecorded(record, owner.userId, owner.publicKey)
      ? [
          {
            ...entry,
            // Whose it is, from the scope's own answer: the record's ML-KEM
            // key for it is what's wrapped to (D338).
            userId: owner.userId,
            mlkemPublicKey:
              entry.mlkemPublicKey !== undefined
                ? entry.mlkemPublicKey
                : owner.mlkemPublicKey,
          },
        ]
      : [];
  });
}

function runShare(): Promise<Handed> {
  if (sharing) {
    if (!nextSharing) {
      nextSharing = sharing.then(async (first) => {
        nextSharing = null;
        const second = await runShare();
        return {
          chats: new Set([...first.chats, ...second.chats]),
          devices: new Set([...first.devices, ...second.devices]),
        };
      });
    }
    return nextSharing;
  }
  sharing = (async (): Promise<Handed> => {
    const chats = new Set<string>();
    const devices = new Set<string>();
    await ready;
    if (!device) return nothingHanded();
    const me = device;
    const pending = await api.getPendingKeyShares(me.id);
    const data = pending.data;
    noteGraceAnswer((pending as { pqGraceOver?: boolean }).pqGraceOver);
    const groups = new Map<string, typeof data>();
    for (const entry of data) {
      const cacheId = keyId(entry.scope, entry.scopeId, entry.epoch);
      groups.set(cacheId, [...(groups.get(cacheId) ?? []), entry]);
    }
    for (const entries of groups.values()) {
      const { scope, scopeId, epoch } = entries[0]!;
      const key = await keyFor(scope, scopeId, epoch);
      if (!key) continue;
      const targets = await recordedTargets(scope, scopeId, entries);
      if (targets.length === 0) continue;
      // In chunks that let the page breathe: a share run can be hundreds
      // of ML-KEM encapsulations on a slow phone (§9).
      const keys: Array<{ deviceKeyId: string; wrappedKey: string }> = [];
      for (const [i, entry] of targets.entries()) {
        if (i > 0 && i % 16 === 0) await breathe();
        const wrappedKey = await wrapFor(key, entry, scope, scopeId, epoch);
        if (wrappedKey)
          keys.push({ deviceKeyId: entry.deviceKeyId, wrappedKey });
      }
      if (keys.length === 0) continue;
      const shared = await api
        .shareKeys(scope, scopeId, { epoch, wrappedByDeviceId: me.id, keys })
        .then(() => true)
        .catch(() => false);
      if (shared) {
        if (scope === 'chat') chats.add(scopeId);
        targets.forEach((entry) => devices.add(entry.deviceKeyId));
      }
    }
    return { chats, devices };
  })()
    .catch(nothingHanded)
    .finally(() => {
      sharing = null;
    });
  return sharing;
}
